import axios, { AxiosError, AxiosInstance } from 'axios';
import { resolveApiMessage } from './apiMessage';
import type {
    ApiResponse,
    ApiError,
    LoginCredentials,
    AuthResponse,
    User,
    Institute,
    Question,
    QuestionCreateDTO,
    Rubric,
    RubricCatalogItem,
    TestSession,
    Submission,
    TestResult,
    StudentAnalytics,
    InstituteAnalytics,
    InstituteDeviceLicensingSummary,
    PaginatedResponse,
    QueryParams,
    AutosavePayload,
} from '@/types';

function isElectronRuntime() {
    return typeof window !== 'undefined' && !!window.examClient?.server?.localApiCall;
}

function electronLoginPath() {
    return window.location.pathname.includes('/login') ? '/login' : '/#/login';
}

function resolveElectronError(error: any) {
    const message = resolveApiMessage({
        error: {
            code: error?.code || error?.error?.code,
            message: error?.error?.message || error?.error || error?.message,
        },
        message: error?.message,
        status: error?.status,
    }, 'Request failed');
    const wrapped = new Error(message) as Error & { response?: { data: any; status?: number } };
    wrapped.response = {
        status: error?.status,
        data: {
            success: false,
            error: {
                message,
                code: error?.code || error?.error?.code,
            },
        },
    };
    return wrapped;
}

function buildElectronPath(config: any) {
    const base = String(config.url || '');
    const params = config.params ? new URLSearchParams(config.params as Record<string, string>).toString() : '';
    if (!params) return base;
    return `${base}${base.includes('?') ? '&' : '?'}${params}`;
}

function parseElectronBody(data: any) {
    if (data == null || data === '') return undefined;
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch {
            return data;
        }
    }
    return data;
}

// ============================================
// API Client Configuration
// ============================================

const API_BASE_URL = normalizeApiBaseUrl(import.meta.env.VITE_API_URL);
const API_BASE_URL_OVERRIDE_KEY = 'api-base-url-override';

function buildRejectedApiError(error: AxiosError<ApiError>, message: string) {
    const payload = (error.response?.data || {}) as any;
    const wrapped = new Error(message) as Error & {
        status?: number;
        code?: string;
        error?: { message: string; code?: string };
        response?: { status?: number; data: any };
    };
    wrapped.status = error.response?.status;
    wrapped.code = payload?.error?.code;
    wrapped.error = { message, code: payload?.error?.code };
    wrapped.response = {
        status: error.response?.status,
        data: {
            ...payload,
            success: false,
            error: {
                ...(typeof payload?.error === 'object' ? payload.error : {}),
                code: payload?.error?.code,
                message,
            },
        },
    };
    return wrapped;
}

export const apiClient: AxiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
    adapter: isElectronRuntime()
        ? async (config) => {
            const path = buildElectronPath(config);
            const method = String(config.method || 'GET').toUpperCase();
            const result = await window.examClient!.server!.localApiCall!({
                method,
                path,
                body: parseElectronBody(config.data),
                // Security-sensitive writes (password change/reset, super-admin login)
                // opt in via { forceRemote: true } in the request config — see those
                // authApi methods below. Never queue-and-fake-success for those.
                forceRemote: (config as any).forceRemote === true,
            });
            if (!result?.success) {
                throw resolveElectronError(result);
            }
            return {
                data: {
                    success: true,
                    data: result.data,
                    ...(result.pagination ? { pagination: result.pagination } : {}),
                    ...(result.summary ? { summary: result.summary } : {}),
                },
                status: 200,
                statusText: 'OK',
                headers: {},
                config,
                request: null,
            };
        }
        : undefined,
});

function normalizeApiBaseUrl(value?: string) {
    const base = (value || '').trim();
    if (!base || base === '/') return '';
    return base.replace(/\/+$/, '');
}

function resolveApiBaseUrl() {
    if (typeof window === 'undefined') return API_BASE_URL;
    const override = normalizeApiBaseUrl(localStorage.getItem(API_BASE_URL_OVERRIDE_KEY) || undefined);
    return override || API_BASE_URL;
}

export function setApiBaseUrlOverride(baseUrl: string | null) {
    if (typeof window === 'undefined') return;
    const normalized = normalizeApiBaseUrl(baseUrl || undefined);
    if (normalized) {
        localStorage.setItem(API_BASE_URL_OVERRIDE_KEY, normalized);
    } else {
        localStorage.removeItem(API_BASE_URL_OVERRIDE_KEY);
    }
}

export function clearApiBaseUrlOverride() {
    setApiBaseUrlOverride(null);
}

// ============================================
// Global admission-session scope
// ============================================
// The institute portal header carries a session selector (store/sessionStore).
// Every institute-scoped list/report endpoint accepts `session_id` (absent =
// all sessions), so rather than thread the param through ~200 helpers we attach
// it here to every institute GET. Pages still re-run their fetches when the
// selected session changes (they read useActiveSessionId()).
//
// localStorage (not an import of the store) is used to keep api.ts free of a
// circular dependency on the store, which itself imports this module.
const SESSION_SCOPE_EXEMPT = [
    '/api/auth',
    '/api/super-admin',
    '/api/superadmin',
    '/api/institute-admin/sessions',
    '/api/institutes',
    '/api/health',
];

// A STUDENT'S OWN ACCOUNT is not a session, and must never be filtered by one.
//
// These endpoints address one named person by id. Scoping them to the session
// selected in the header produced a Student Finance Workspace that contradicted
// itself: the summary (which is not session-scoped) read Total ₹12,000, Paid
// ₹8,000, Balance ₹4,000, while below it said "No enrollments found for this
// student" and "No payment history available" — because that student's
// enrollment belongs to Winter 2026 and the header was showing Summer 2027.
//
// The real damage is not the contradiction. A balance does not stop being owed
// because the academic session rolled over, so the ₹4,000 was invisible AND
// uncollectable: the desk could not see the enrollment, so it could not take
// the money. Anyone enrolled in an earlier session simply disappeared.
//
// Lists stay scoped. Browsing an institute's payments or students is a
// session-by-session view and should remain one; it is only the per-student
// account that must show the whole person.
const PER_STUDENT_ACCOUNT_ENDPOINTS = [
    /^\/api\/finance\/enrollments\/[^/]+$/,
    /^\/api\/finance\/payments\/student\//,
    /^\/api\/finance\/students\/[^/]+\/financial-summary/,
    // The collections worklist. Unpaid fees are not an academic question:
    // scoping this to the selected session hid 40 students owing ₹238,500
    // behind a ledger that read "3 enrollments", so the money could not be
    // chased because nobody could see who owed it.
    /^\/api\/institute-admin\/student-enrollment-status/,
];

function sessionScopedRoleActive(): boolean {
    try {
        const authData = localStorage.getItem('auth-storage');
        if (!authData) return false;
        const { state } = JSON.parse(authData);
        const role = state?.user?.role;
        if (!role) return false;
        // Super admins browse across institutes; students have no portal scope.
        return role !== 'super_admin' && role !== 'student';
    } catch {
        return false;
    }
}

function activeSessionScopeId(): string {
    try {
        return localStorage.getItem('activeSessionId') || '';
    } catch {
        return '';
    }
}

export function attachSessionScope(config: any) {
    if (String(config.method || 'get').toLowerCase() !== 'get') return config;
    const url = String(config.url || '');
    if (!url.startsWith('/api/')) return config;
    if (SESSION_SCOPE_EXEMPT.some((p) => url.startsWith(p))) return config;
    if (PER_STUDENT_ACCOUNT_ENDPOINTS.some((re) => re.test(url.split('?')[0]))) return config;
    if (url.includes('session_id=')) return config;
    if (config.params && (config.params as any).session_id) return config;
    if (!sessionScopedRoleActive()) return config;
    const sessionId = activeSessionScopeId();
    if (!sessionId) return config;
    config.params = { ...(config.params || {}), session_id: sessionId };
    return config;
}

// Request interceptor - add auth token
apiClient.interceptors.request.use(
    (config) => {
        const resolvedBaseUrl = resolveApiBaseUrl();
        if (resolvedBaseUrl !== undefined) {
            config.baseURL = resolvedBaseUrl;
        }
        const authData = localStorage.getItem('auth-storage');
        if (authData) {
            const { state } = JSON.parse(authData);
            if (state?.token) {
                config.headers.Authorization = `Bearer ${state.token}`;
            }
        }
        attachSessionScope(config);
        return config;
    },
    (error) => Promise.reject(error)
);


// Refresh token queue to handle multiple concurrent 401s
let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: any) => void }> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token!);
        }
    });
    failedQueue = [];
};

// Response interceptor - handle errors
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<ApiError>) => {
        const normalizedMessage = resolveApiMessage({
            error: {
                code: error.response?.data?.error?.code,
                message: error.response?.data?.error?.message || (typeof error.response?.data?.error === 'string' ? error.response?.data?.error : undefined),
            },
            message: error.message,
            status: error.response?.status,
        }, 'Request failed');
        if (error.response?.data) {
            if (!error.response.data.error || typeof error.response.data.error === 'string') {
                error.response.data.error = { message: normalizedMessage, code: error.response?.data?.error?.code } as any;
            } else {
                error.response.data.error.message = normalizedMessage;
            }
        }
        error.message = normalizedMessage;
        if (isElectronRuntime()) {
            if ((error as any)?.response?.status === 401 && !window.location.hash.includes('/login')) {
                localStorage.removeItem('auth-storage');
                window.location.href = electronLoginPath();
            }
            return Promise.reject(error);
        }
        const originalRequest = error.config as any;

        // If 401 and not already retrying
        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login')) {
            if (isRefreshing) {
                return new Promise<string>((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers.Authorization = `Bearer ${token}`;
                        return apiClient(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const authData = localStorage.getItem('auth-storage');
                if (!authData) throw new Error('No auth data');

                const { state } = JSON.parse(authData);
                const currentToken = state?.token;

                if (!currentToken) throw new Error('No valid token');

                // Call refresh endpoint directly
                // Note: We use axios directly to avoid this interceptor
                const { data } = await axios.post(`${resolveApiBaseUrl()}/api/auth/refresh`, {
                    token: currentToken,
                });

                if (data.success && data.data.token) {
                    const { token: newToken, user: newUser } = data.data;

                    // Update Local Storage directly
                    const parsed = JSON.parse(authData);
                    parsed.state.token = newToken;
                    if (newUser) parsed.state.user = newUser;
                    localStorage.setItem('auth-storage', JSON.stringify(parsed));

                    // Process queue
                    processQueue(null, newToken);
                    isRefreshing = false;

                    // Retry original request
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return apiClient(originalRequest);
                } else {
                    throw new Error('Refresh failed');
                }
            } catch (refreshError) {
                processQueue(refreshError, null);
                isRefreshing = false;

                // Send them to the login ROUTE, not to an absolute path.
                //
                // This used to be `window.location.href = '/login'`, and in the
                // Institute EXE the app is loaded over file:// -- so an absolute
                // path is not the login route, it is the ROOT OF THE DRIVE. One
                // expired session navigated the window to file:///D:/ and the
                // whole portal was gone: no #root, no content, no way back
                // without restarting the application. Found by crawling the
                // EXE's own screens, where nine consecutive pages read as empty
                // because the app was no longer there.
                //
                // On the website the same line appeared to work, which is
                // exactly why it survived: the surface it breaks on is the one
                // nobody loads in a browser.
                //
                // Assigning the hash keeps the document -- and therefore the
                // app -- and lets the router do the navigating, on both
                // surfaces.
                //
                // The guard reads the HASH too. It tested location.pathname for
                // '/login' before, and this app routes by hash, so pathname is
                // always the index.html path and never contains it: the guard
                // could not fire, and every failed refresh triggered another
                // navigation.
                // Navigate the way THIS surface's router understands, because
                // they are not the same router. main.tsx picks
                //     isElectronRuntime ? HashRouter : BrowserRouter
                // so the EXE routes by hash and the website routes by path.
                //
                // This was `window.location.href = '/login'` for both. In the
                // EXE the app is loaded over file://, where an absolute path is
                // not the login route but the ROOT OF THE DRIVE: one expired
                // session navigated the window to file:///D:/ and the entire
                // portal was gone -- no #root, no content, no way back without
                // restarting. Found by crawling the EXE's own screens, where
                // nine consecutive routes read as empty because the app was no
                // longer there. On the website the same line worked, which is
                // why it lasted: the surface it breaks is the one nobody opens
                // in a browser.
                //
                // Assigning the hash in the EXE keeps the document, and so
                // keeps the app, instead of replacing it with a directory.
                //
                // The guard matches the surface too. It read location.pathname
                // for '/login', which in a hash-routed app is always the
                // index.html path and never contains the route -- so it could
                // not fire, and every failed refresh triggered another
                // navigation.
                const inExe = isElectronRuntime();
                const alreadyAtLogin = inExe
                    ? String(window.location.hash || '').startsWith('#/login')
                    : window.location.pathname.startsWith('/login');
                if (!alreadyAtLogin) {
                    console.error('Session expired, logging out:', refreshError);
                    localStorage.removeItem('auth-storage');
                    if (inExe) window.location.hash = '#/login';
                    else window.location.href = '/login';
                }
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(buildRejectedApiError(error, normalizedMessage));
    }
);

// ============================================
// Helper Functions
// ============================================

function normalizeRepeaterFees(repeater?: { repeater_fee?: number | string | null; repeater_license_fee?: number | string | null }) {
    if (!repeater) return {};
    const norm = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
    const out: Record<string, number | null> = {};
    if ('repeater_fee' in repeater) out.repeater_fee = norm(repeater.repeater_fee);
    if ('repeater_license_fee' in repeater) out.repeater_license_fee = norm(repeater.repeater_license_fee);
    return out;
}

function buildQueryString(params?: QueryParams): string {
    if (!params) return '';

    const searchParams = new URLSearchParams();

    if (params.page) searchParams.set('page', String(params.page));
    if (params.per_page) searchParams.set('per_page', String(params.per_page));
    if (params.sort_by) searchParams.set('sort_by', params.sort_by);
    if (params.sort_order) searchParams.set('sort_order', params.sort_order);
    if (params.search) searchParams.set('search', params.search);
    if (params.role) searchParams.set('role', params.role);
    if (params.difficulty) searchParams.set('difficulty', params.difficulty);
    if (params.status) searchParams.set('status', params.status);
    if (params.approved) searchParams.set('approved', params.approved);
    if (params.enrollment_type) searchParams.set('enrollment_type', params.enrollment_type);

    if (params.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach((v) => searchParams.append(key, v));
            } else {
                searchParams.set(key, value);
            }
        });
    }

    const query = searchParams.toString();
    return query ? `?${query}` : '';
}

// ============================================
// Auth API
// ============================================

export const authApi = {
    login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
        if (isElectronRuntime()) {
            const result = await window.examClient!.server!.login!({
                email: credentials.email,
                password: credentials.password,
            });
            if (!result?.success || !result?.token || !result?.user) {
                throw resolveElectronError(result || { error: 'Login failed', status: 401 });
            }
            return {
                token: result.token,
                user: result.user,
                expires_at: result.expires_at || result.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            };
        }
        const { data } = await apiClient.post<ApiResponse<AuthResponse>>('/api/auth/login', credentials);
        return data.data;
    },

    // Browser lab login (learn.eswift.in/lab/login) — institute code + student id +
    // password, always against the cloud.
    labLogin: async (payload: { institute_code: string; email: string; password: string }): Promise<AuthResponse> => {
        const { data } = await apiClient.post<ApiResponse<AuthResponse>>('/api/auth/lab-login', payload, { forceRemote: true } as any);
        return data.data;
    },

    getPublicConfig: async (): Promise<any> => {
        if (isElectronRuntime()) {
            return {
                branding: {
                    name: 'Institute EXE',
                    display_name: 'Institute EXE',
                    logo_url: null,
                },
                captcha: {
                    provider: 'none',
                    enabled: false,
                    site_key: '',
                    verify_on_admin_login: false,
                    verify_on_student_login: false,
                    supported: false,
                },
            };
        }
        const { data } = await apiClient.get<ApiResponse<any>>('/api/auth/public-config');
        return data.data;
    },

    superAdminLogin: async (password: string, turnstile_token?: string): Promise<{ requires_otp: boolean; otp_session_id?: string; email_masked?: string } & Partial<AuthResponse>> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/auth/super-admin/login', { password, turnstile_token }, { forceRemote: true } as any);
        return data.data;
    },

    superAdminVerifyOtp: async (otp_session_id: string, otp: string): Promise<AuthResponse> => {
        const { data } = await apiClient.post<ApiResponse<AuthResponse>>('/api/auth/super-admin/verify-otp', { otp_session_id, otp }, { forceRemote: true } as any);
        return data.data;
    },

    refresh: async (token: string): Promise<AuthResponse> => {
        if (isElectronRuntime()) {
            const result = await window.examClient!.server!.getAuthSession!();
            if (!result?.success || !result?.token || !result?.user) {
                throw resolveElectronError(result || { error: 'Session expired', status: 401 });
            }
            return {
                token: result.token,
                user: result.user,
                expires_at: result.expires_at || result.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            };
        }
        const { data } = await apiClient.post<ApiResponse<AuthResponse>>('/api/auth/refresh', { token });
        return data.data;
    },

    logout: async (): Promise<void> => {
        if (isElectronRuntime()) {
            await window.examClient!.server!.logout!();
            return;
        }
        await apiClient.post('/api/auth/logout');
    },

    me: async (): Promise<User> => {
        if (isElectronRuntime()) {
            const result = await window.examClient!.server!.getAuthSession!();
            if (!result?.success || !result?.user) {
                throw resolveElectronError(result || { error: 'Not logged in', status: 401 });
            }
            return result.user;
        }
        const { data } = await apiClient.get<ApiResponse<User>>('/api/auth/me');
        return data.data;
    },

    changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
        await apiClient.post('/api/auth/change-password', {
            currentPassword,
            newPassword,
            confirmPassword: newPassword
        }, { forceRemote: true } as any);
    },

    forgotPassword: async (identifier: string): Promise<{ message: string }> => {
        const { data } = await apiClient.post<ApiResponse<{ message: string }>>('/api/auth/forgot-password', { identifier }, { forceRemote: true } as any);
        return data.data;
    },

    resetPassword: async (token: string, newPassword: string): Promise<{ message: string }> => {
        const { data } = await apiClient.post<ApiResponse<{ message: string }>>('/api/auth/reset-password', {
            token,
            newPassword,
            confirmPassword: newPassword,
        }, { forceRemote: true } as any);
        return data.data;
    },
};

// ============================================
// Users API
// ============================================

export const usersApi = {
    list: async (params?: QueryParams): Promise<PaginatedResponse<User>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<User>>>(`/api/users${query}`);
        return data.data;
    },

    get: async (id: string): Promise<User> => {
        const { data } = await apiClient.get<ApiResponse<User>>(`/api/users/${id}`);
        return data.data;
    },

    create: async (userData: Partial<User> & { password: string; institute_id?: string }): Promise<User> => {
        const { data } = await apiClient.post<ApiResponse<User>>('/api/users', userData);
        return data.data;
    },

    update: async (id: string, userData: Partial<User> & { password?: string }): Promise<User> => {
        const { data } = await apiClient.put<ApiResponse<User>>(`/api/users/${id}`, userData);
        return data.data;
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/users/${id}`);
    },

    resetPassword: async (id: string): Promise<{ temporaryPassword: string }> => {
        const { data } = await apiClient.post<ApiResponse<{ temporaryPassword: string }>>(`/api/users/${id}/reset-password`, undefined, { forceRemote: true } as any);
        return data.data;
    },

    setUsername: async (id: string, username: string): Promise<{ id: string; username: string }> => {
        // Username uniqueness is a global constraint — must always go to the live
        // cloud, never a local/offline echo (forceRemote), or the Server EXE would
        // fake-succeed then fail on sync.
        const { data } = await apiClient.put<ApiResponse<{ id: string; username: string }>>(`/api/users/${id}/username`, { username }, { forceRemote: true } as any);
        return data.data;
    },

    checkUsername: async (username: string, excludeUserId?: string): Promise<{ available: boolean }> => {
        // Always check against the live cloud — a stale/absent local cache in the
        // Server EXE was making every username read as "already taken".
        const ex = excludeUserId ? `&exclude_user_id=${encodeURIComponent(excludeUserId)}` : '';
        const { data } = await apiClient.get<ApiResponse<{ available: boolean }>>(`/api/users/check-username?username=${encodeURIComponent(username)}${ex}`, { forceRemote: true } as any);
        return data.data;
    },

    checkEmail: async (email: string, excludeUserId?: string): Promise<{ available: boolean }> => {
        const ex = excludeUserId ? `&exclude_user_id=${encodeURIComponent(excludeUserId)}` : '';
        const { data } = await apiClient.get<ApiResponse<{ available: boolean }>>(`/api/users/check-email?email=${encodeURIComponent(email)}${ex}`, { forceRemote: true } as any);
        return data.data;
    },

    lockToggle: async (id: string, lock: boolean, reason?: string): Promise<{ locked: boolean; user_id: string }> => {
        const { data } = await apiClient.put<ApiResponse<{ locked: boolean; user_id: string }>>(`/api/users/${id}/lock`, { lock, reason });
        return data.data;
    },

    updateStudentStatus: async (id: string, status: 'active' | 'inactive' | 'flagged', reason?: string): Promise<{ user_id: string; status: string }> => {
        const { data } = await apiClient.put<ApiResponse<{ user_id: string; status: string }>>(`/api/users/${id}/status`, { status, reason });
        return data.data;
    },

    updateMyProfile: async (profile: Record<string, any>): Promise<User> => {
        const { data } = await apiClient.put<ApiResponse<User>>('/api/users/me/profile', { profile });
        return data.data;
    },
};

// ============================================
// Institutes API
// ============================================

export const institutesApi = {
    list: async (params?: QueryParams): Promise<PaginatedResponse<Institute>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<Institute>>>(`/api/institutes${query}`);
        return data.data;
    },

    get: async (id: string): Promise<Institute> => {
        const { data } = await apiClient.get<ApiResponse<Institute>>(`/api/institutes/${id}`);
        return data.data;
    },

    create: async (instituteData: Partial<Institute>): Promise<Institute> => {
        const { data } = await apiClient.post<ApiResponse<Institute>>('/api/institutes', instituteData);
        return data.data;
    },

    update: async (id: string, instituteData: Partial<Institute>): Promise<Institute> => {
        const { data } = await apiClient.put<ApiResponse<Institute>>(`/api/institutes/${id}`, instituteData);
        return data.data;
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institutes/${id}`);
    },

    flag: async (id: string, flagged: boolean, reason?: string): Promise<void> => {
        await apiClient.patch(`/api/institutes/${id}/flag`, { flagged, reason: reason || '' });
    },

    // ── Controlled online-login system (superadmin only) ──────────────────────
    // Master switch: enable/disable an institute's online (web/lab) student login.
    setOnlineLogin: async (id: string, enabled: boolean): Promise<void> => {
        await apiClient.patch(`/api/institutes/${id}/online-login`, { enabled });
    },
    // Per-student switch (only takes effect once the institute switch is ON).
    setStudentOnlineLogin: async (instituteId: string, studentId: string, enabled: boolean): Promise<void> => {
        await apiClient.patch(`/api/institutes/${instituteId}/students/${studentId}/online-login`, { enabled });
    },
    // Enable/disable ALL students of an institute at once.
    bulkSetStudentsOnlineLogin: async (id: string, enabled: boolean): Promise<{ affected: number }> => {
        const { data } = await apiClient.patch<ApiResponse<{ affected: number }>>(`/api/institutes/${id}/students-online-login/bulk`, { enabled });
        return data.data;
    },
    // Superadmin: allow/deny an institute from uploading content (learning library).
    setContentUpload: async (id: string, enabled: boolean): Promise<void> => {
        await apiClient.patch(`/api/institutes/${id}/content-upload`, { enabled });
    },

    // Superadmin kill-switch for the institute's Server EXE (takes effect on the
    // EXE's next cloud sync; lifts automatically after unblock + next sync).
    setServerExeBlock: async (id: string, blocked: boolean, reason?: string): Promise<void> => {
        await apiClient.patch(`/api/institutes/${id}/server-exe-block`, { blocked, reason: reason || '' });
    },

    sendOtp: async (email: string): Promise<{ otp_token: string; debug_otp?: string }> => {
        const { data } = await apiClient.post<ApiResponse<{ otp_token: string; debug_otp?: string }>>('/api/institutes/send-otp', { email });
        return data.data;
    },

    checkAvailability: async (params: {
        code?: string;
        institute_email?: string;
        admin_email?: string;
        exclude_institute_id?: string;
    }): Promise<{ code_available: boolean; institute_email_available: boolean; admin_email_available: boolean }> => {
        const query = params
            ? `?${new URLSearchParams(
                Object.entries(params)
                    .filter(([, value]) => value !== undefined && value !== null && value !== '')
                    .map(([key, value]) => [key, String(value)])
            ).toString()}`
            : '';
        const { data } = await apiClient.get<ApiResponse<{ code_available: boolean; institute_email_available: boolean; admin_email_available: boolean }>>(`/api/institutes/check-availability${query}`, { forceRemote: true } as any);
        return data.data;
    },

    getAnalytics: async (id: string, params?: { enrollment_type?: string }): Promise<InstituteAnalytics> => {
        const qs = params?.enrollment_type ? `?enrollment_type=${encodeURIComponent(params.enrollment_type)}` : '';
        const { data } = await apiClient.get<ApiResponse<InstituteAnalytics>>(`/api/institutes/${id}/analytics${qs}`);
        return data.data;
    },

    getStudents: async (id: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institutes/${id}/students`);
        return data.data;
    },

    addStudent: async (instituteId: string, student: {
        first_name: string; last_name?: string; email: string; password: string;
        phone?: string; date_of_birth?: string; session_id?: string; gr_number?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${instituteId}/students`, student);
        return data.data;
    },

    getDeviceLicensing: async (id: string): Promise<InstituteDeviceLicensingSummary> => {
        const { data } = await apiClient.get<ApiResponse<InstituteDeviceLicensingSummary>>(`/api/institutes/${id}/device-licensing`);
        return data.data;
    },

    issueDeviceLicenseKey: async (id: string, payload: { label?: string | null; allowed_device_limit?: number | null }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/device-licensing/keys`, payload);
        return data.data;
    },

    rotateDeviceLicenseKey: async (id: string, keyId: string, payload: { label?: string | null; allowed_device_limit?: number | null }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/device-licensing/keys/${keyId}/rotate`, payload);
        return data.data;
    },

    registerDevice: async (id: string, payload: {
        license_key_id?: string | null;
        device_fingerprint: string;
        hostname?: string | null;
        mac_address?: string | null;
        notes?: string | null;
        machine_metadata?: Record<string, any> | null;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/device-licensing/devices`, payload);
        return data.data;
    },

    releaseDevice: async (id: string, deviceId: string, reason?: string | null): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/device-licensing/devices/${deviceId}/release`, { reason: reason || null });
        return data.data;
    },

    lockDevice: async (id: string, deviceId: string, reason?: string | null): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/device-licensing/devices/${deviceId}/lock`, { reason: reason || null });
        return data.data;
    },

    reenableDevice: async (id: string, deviceId: string, reason?: string | null): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/device-licensing/devices/${deviceId}/re-enable`, { reason: reason || null });
        return data.data;
    },

    // All-institutes device licensing summary (super admin)
    getDeviceLicensingSummary: async (): Promise<{ institutes: any[]; totals: any }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institutes/device-licensing/summary');
        return data.data;
    },

    // License fee payment gateway (platform-managed, not per-institute)
    getLicenseFeeGatewayConfig: async (id: string): Promise<{ provider: string; key_id: string; enabled: boolean }> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institutes/${id}/license-fees/gateway-config`);
        return data.data;
    },

    createLicenseFeeOrder: async (id: string, payload: { amount: number; description?: string; ledger_id?: string; ledger_ids?: string[] }): Promise<{
        provider: string; order_id: string; amount: number; currency: string; key_id: string; institute_name: string; description: string;
    }> => {
        // License fees are real money paid to the platform via the gateway — the
        // order/verify MUST hit the live cloud (forceRemote), never the Server EXE's
        // offline outbox, which would fake-succeed a payment that never happened.
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/license-fees/order`, payload, { forceRemote: true } as any);
        return data.data;
    },

    verifyLicenseFeePayment: async (id: string, payload: {
        amount: number;
        razorpay_order_id: string;
        razorpay_payment_id: string;
        razorpay_signature: string;
        ledger_id?: string;
        ledger_ids?: string[];
        description?: string;
    }): Promise<{ payment_id: string; order_id: string; amount: number; message: string; receipts?: Array<{ ledger_id: string; receipt_number: string | null; paid_amount: number; balance: number; status: string; student_id: string | null; student_name: string | null; subject_name: string | null }> }> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institutes/${id}/license-fees/verify`, payload, { forceRemote: true } as any);
        return data.data;
    },
};

// ============================================
// WEB LAB LOGIN AUDIT (super admin only)
// ============================================
export const labLoginApi = {
    list: async (params?: { success?: boolean; institute_id?: string; search?: string; limit?: number }): Promise<any[]> => {
        const q = new URLSearchParams();
        if (params?.success !== undefined) q.set('success', String(params.success));
        if (params?.institute_id) q.set('institute_id', params.institute_id);
        if (params?.search) q.set('search', params.search);
        if (params?.limit) q.set('limit', String(params.limit));
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/super-admin/lab-login-events${q.toString() ? `?${q}` : ''}`);
        return data.data ?? [];
    },
};

// ============================================
// SERVER EXE LICENSE API (super admin only)
// ============================================
export const serverLicenseApi = {
    getByInstitute: async (instituteId: string): Promise<any | null> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/super-admin/server-licenses/by-institute/${instituteId}`);
        return data.data;
    },
    generate: async (instituteId: string, payload?: { label?: string; notes?: string }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/super-admin/server-licenses/generate', {
            institute_id: instituteId,
            ...payload,
        });
        return data.data;
    },
    block: async (licenseId: string, notes?: string): Promise<void> => {
        await apiClient.post(`/api/super-admin/server-licenses/${licenseId}/block`, { notes: notes ?? null });
    },
    unblock: async (licenseId: string): Promise<void> => {
        await apiClient.post(`/api/super-admin/server-licenses/${licenseId}/unblock`, {});
    },
    revoke: async (licenseId: string): Promise<void> => {
        await apiClient.delete(`/api/super-admin/server-licenses/${licenseId}`);
    },
    regenerate: async (licenseId: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/super-admin/server-licenses/${licenseId}/regenerate`, {});
        return data.data;
    },
};

// ============================================
// Questions API
// ============================================

export const questionsApi = {
    list: async (params?: QueryParams): Promise<PaginatedResponse<Question>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<Question>>>(`/api/questions${query}`);
        return data.data;
    },

    get: async (id: string): Promise<Question> => {
        const { data } = await apiClient.get<ApiResponse<Question>>(`/api/questions/${id}`);
        return data.data;
    },

    create: async (questionData: QuestionCreateDTO): Promise<Question> => {
        const { data } = await apiClient.post<ApiResponse<Question>>('/api/questions', questionData);
        return data.data;
    },

    update: async (id: string, questionData: Partial<QuestionCreateDTO>): Promise<Question> => {
        const { data } = await apiClient.put<ApiResponse<Question>>(`/api/questions/${id}`, questionData);
        return data.data;
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/questions/${id}`);
    },

    approve: async (id: string): Promise<Question> => {
        const { data } = await apiClient.post<ApiResponse<Question>>(`/api/questions/${id}/approve`);
        return data.data;
    },

    reject: async (id: string, reason: string): Promise<void> => {
        await apiClient.post(`/api/questions/${id}/reject`, { reason });
    },
};

// ============================================
// Rubrics API
// ============================================

export const rubricsApi = {
    catalog: async (): Promise<RubricCatalogItem[]> => {
        const { data } = await apiClient.get<ApiResponse<RubricCatalogItem[]>>('/api/rubrics/catalog');
        return data.data;
    },

    list: async (params?: QueryParams): Promise<PaginatedResponse<Rubric>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<Rubric>>>(`/api/rubrics${query}`);
        return data.data;
    },

    get: async (id: string): Promise<Rubric> => {
        const { data } = await apiClient.get<ApiResponse<Rubric>>(`/api/rubrics/${id}`);
        return data.data;
    },

    create: async (rubricData: Partial<Rubric>): Promise<Rubric> => {
        const { data } = await apiClient.post<ApiResponse<Rubric>>('/api/rubrics', rubricData);
        return data.data;
    },

    update: async (id: string, rubricData: Partial<Rubric>): Promise<Rubric> => {
        const { data } = await apiClient.put<ApiResponse<Rubric>>(`/api/rubrics/${id}`, rubricData);
        return data.data;
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/rubrics/${id}`);
    },
};

// ============================================
// Test Sessions API
// ============================================

export const testSessionsApi = {
    list: async (params?: QueryParams): Promise<PaginatedResponse<TestSession>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<TestSession>>>(`/api/test-sessions${query}`);
        return data.data;
    },

    get: async (id: string): Promise<TestSession> => {
        const { data } = await apiClient.get<ApiResponse<TestSession>>(`/api/test-sessions/${id}`);
        return data.data;
    },

    start: async (questionId: string): Promise<TestSession> => {
        const { data } = await apiClient.post<ApiResponse<TestSession>>('/api/test-sessions/start', { questionId });
        return data.data;
    },

    submit: async (sessionId: string, content: { html: string; text: string }): Promise<Submission> => {
        const { data } = await apiClient.post<ApiResponse<Submission>>(`/api/test-sessions/${sessionId}/submit`, content);
        return data.data;
    },

    autosave: async (payload: AutosavePayload): Promise<void> => {
        await apiClient.post(`/api/test-sessions/${payload.session_id}/autosave`, payload);
    },

    getResult: async (sessionId: string): Promise<TestResult> => {
        const { data } = await apiClient.get<ApiResponse<TestResult>>(`/api/test-sessions/${sessionId}/result`);
        return data.data;
    },

    getAvailable: async (): Promise<TestSession[]> => {
        const { data } = await apiClient.get<ApiResponse<TestSession[]>>('/api/test-sessions/available');
        return data.data;
    },

    getHistory: async (params?: QueryParams): Promise<PaginatedResponse<TestSession>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<TestSession>>>(`/api/test-sessions/history${query}`);
        return data.data;
    },
};

// ============================================
// Analytics API
// ============================================

export const analyticsApi = {
    getStudentAnalytics: async (studentId?: string): Promise<StudentAnalytics> => {
        const url = studentId ? `/api/analytics/student/${studentId}` : '/api/analytics/student';
        const { data } = await apiClient.get<ApiResponse<StudentAnalytics>>(url);
        return data.data;
    },

    getInstituteAnalytics: async (instituteId?: string, filters?: { year?: number | null; month?: number | null; session_id?: string | null; start_date?: string | null; end_date?: string | null; enrollment_type?: string | null }): Promise<InstituteAnalytics> => {
        const params: Record<string, string> = {};
        if (instituteId) params.institute_id = instituteId;
        if (filters?.enrollment_type) params.enrollment_type = filters.enrollment_type;
        if (filters?.year) params.year = String(filters.year);
        if (filters?.month) params.month = String(filters.month);
        if (filters?.session_id) params.session_id = filters.session_id;
        if (filters?.start_date) params.start_date = filters.start_date;
        if (filters?.end_date) params.end_date = filters.end_date;
        const { data } = await apiClient.get<ApiResponse<InstituteAnalytics>>('/api/analytics/institute', { params });
        return data.data;
    },

    exportReport: async (type: 'student' | 'institute', format: 'csv' = 'csv'): Promise<Blob> => {
        const { data } = await apiClient.get(`/api/analytics/export`, {
            params: { type, format },
            responseType: 'blob',
        });
        return data;
    },
};

// ============================================
// Assignments API
// ============================================

export const assignmentsApi = {
    list: async (params?: QueryParams): Promise<PaginatedResponse<any>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<any>>>(`/api/assignments${query}`);
        return data.data;
    },

    create: async (assignmentData: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/assignments', assignmentData);
        return data.data;
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/assignments/${id}`);
    },
};

// ============================================
// Master Data API (Genders, Handicaps, Courses, Subjects, Identity Docs)
// ============================================

export const masterDataApi = {
    // Genders
    getGenders: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/genders');
        return data.data;
    },
    createGender: async (name: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/genders', { name });
        return data.data;
    },
    updateGender: async (id: string, name: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/genders/${id}`, { name });
        return data.data;
    },
    deleteGender: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/master-data/genders/${id}`);
    },

    // Handicaps
    getHandicaps: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/handicaps');
        return data.data;
    },
    createHandicap: async (name: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/handicaps', { name });
        return data.data;
    },
    updateHandicap: async (id: string, name: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/handicaps/${id}`, { name });
        return data.data;
    },
    deleteHandicap: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/master-data/handicaps/${id}`);
    },

    // Courses
    getCourses: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/courses');
        return data.data;
    },
    createCourse: async (name: string, description?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/courses', { name, description });
        return data.data;
    },
    updateCourse: async (id: string, name: string, description?: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/courses/${id}`, { name, description });
        return data.data;
    },
    deleteCourse: async (id: string, force = false): Promise<void> => {
        await apiClient.delete(`/api/master-data/courses/${id}${force ? '?force=true' : ''}`);
    },

    // Subjects
    getSubjects: async (courseId?: string): Promise<any[]> => {
        const query = courseId ? `?course_id=${courseId}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/master-data/subjects${query}`);
        return data.data;
    },
    // repeater: { repeater_fee, repeater_license_fee } — null/'' = same as regular.
    createSubject: async (name: string, course_id: string, description?: string, fee?: number, license_fee?: number, language?: string, repeater?: { repeater_fee?: number | string | null; repeater_license_fee?: number | string | null }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/subjects', { name, course_id, description, fee: fee ?? 0, license_fee: license_fee ?? 0, language: language || 'english', ...normalizeRepeaterFees(repeater) });
        return data.data;
    },
    updateSubject: async (id: string, name: string, course_id: string, description?: string, fee?: number, license_fee?: number, language?: string, repeater?: { repeater_fee?: number | string | null; repeater_license_fee?: number | string | null }): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/subjects/${id}`, { name, course_id, description, fee: fee ?? 0, license_fee: license_fee ?? 0, language: language || 'english', ...normalizeRepeaterFees(repeater) });
        return data.data;
    },
    deleteSubject: async (id: string, force = false): Promise<void> => {
        await apiClient.delete(`/api/master-data/subjects/${id}${force ? '?force=true' : ''}`);
    },

    // Identity Documents
    getIdentityDocuments: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/identity-documents');
        return data.data;
    },
    createIdentityDocument: async (name: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/identity-documents', { name });
        return data.data;
    },
    updateIdentityDocument: async (id: string, name: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/identity-documents/${id}`, { name });
        return data.data;
    },
    deleteIdentityDocument: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/master-data/identity-documents/${id}`);
    },



    // Admission Sessions
    getAdmissionSessions: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/institute-sessions');
        return data.data;
    },
    createAdmissionSession: async (sessionData: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/institute-sessions', sessionData);
        return data.data;
    },
    updateAdmissionSession: async (id: string, sessionData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/institute-sessions/${id}`, sessionData);
        return data.data;
    },
    deleteAdmissionSession: async (id: string, force = false): Promise<void> => {
        await apiClient.delete(`/api/master-data/institute-sessions/${id}${force ? '?force=true' : ''}`);
    },

    // Session Allotments
    getSessionAllotments: async (params?: { institute_id?: string; session_id?: string }): Promise<any[]> => {
        const q = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/master-data/session-allotments${q}`);
        return data.data;
    },
    createSessionAllotment: async (payload: {
        session_id: string;
        institute_id: string;
        fee_per_student?: number;
        grace_days?: number;
        max_students?: number;
        notes?: string;
    }): Promise<{ data: any; already_existed?: boolean }> => {
        const { data } = await apiClient.post<ApiResponse<any> & { already_existed?: boolean }>('/api/master-data/session-allotments', payload);
        return { data: data.data, already_existed: data.already_existed };
    },
    updateSessionAllotment: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/session-allotments/${id}`, payload);
        return data.data;
    },
    deleteSessionAllotment: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/master-data/session-allotments/${id}`);
    },

    // Institute Student Payments (super admin view)
    getInstituteStudentPayments: async (params?: { institute_id?: string; session_id?: string; status?: string }): Promise<any[]> => {
        const q = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/master-data/institute-student-payments${q}`);
        return data.data;
    },
    verifyInstituteStudentPayment: async (id: string, payload: { status: 'completed' | 'cancelled'; notes?: string }): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/institute-student-payments/${id}/verify`, payload);
        return data.data;
    },
    blockStudentPayment: async (studentId: string, block: boolean, reason?: string): Promise<void> => {
        await apiClient.patch(`/api/master-data/students/${studentId}/payment-block`, { block, reason });
    },
    deactivateStudent: async (studentId: string, reason?: string): Promise<void> => {
        await apiClient.patch(`/api/master-data/students/${studentId}/deactivate`, { reason });
    },

    // Practice Types
    getPracticeTypes: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/practice-types');
        return data.data;
    },
    createPracticeType: async (ptData: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/practice-types', ptData);
        return data.data;
    },
    updatePracticeType: async (id: string, ptData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/practice-types/${id}`, ptData);
        return data.data;
    },
    deletePracticeType: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/master-data/practice-types/${id}`);
    },

    // Exam Types
    getExamTypes: async (params?: { subject_id?: string; course_id?: string }): Promise<any[]> => {
        const qs = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/master-data/exam-types${qs}`);
        return data.data;
    },
    createExamType: async (etData: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/exam-types', etData);
        return data.data;
    },
    updateExamType: async (id: string, etData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/exam-types/${id}`, etData);
        return data.data;
    },
    deleteExamType: async (id: string, force = false): Promise<void> => {
        await apiClient.delete(`/api/master-data/exam-types/${id}${force ? '?force=true' : ''}`);
    },

    // Exam Formats
    getExamFormats: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/exam-formats');
        return data.data;
    },
    createExamFormat: async (efData: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/exam-formats', efData);
        return data.data;
    },
    updateExamFormat: async (id: string, efData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/exam-formats/${id}`, efData);
        return data.data;
    },
    deleteExamFormat: async (id: string, force = false): Promise<void> => {
        await apiClient.delete(`/api/master-data/exam-formats/${id}${force ? '?force=true' : ''}`);
    },

    // NOTE: Trash (soft-delete) removed — this system has no soft-delete. Deletes are
    // permanent; deleteXxx endpoints accept ?force=true to also purge references.

    // Licenses
    getLicenses: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/master-data/licenses');
        return data.data;
    },
    createLicense: async (lData: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/master-data/licenses', lData);
        return data.data;
    },
    updateLicense: async (id: string, lData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/master-data/licenses/${id}`, lData);
        return data.data;
    },
    deleteLicense: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/master-data/licenses/${id}`);
    },
};

// ============================================
// Notices API
// ============================================

export const noticesApi = {
    // `status` narrows by the notice's own date window: active today, already
    // ended, not started yet, or all of them. Omitted means active only, which
    // is what a student always gets regardless — an expired notice must not
    // reach a lab PC.
    list: async (
        type?: 'sent' | 'received',
        options?: { unreadOnly?: boolean; status?: 'active' | 'expired' | 'scheduled' | 'all' },
    ): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (type) searchParams.set('type', type);
        if (options?.unreadOnly) searchParams.set('unread_only', 'true');
        if (options?.status) searchParams.set('status', options.status);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/notices${query}`);
        return data.data;
    },

    getUsers: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/notices/users');
        return data.data;
    },

    getStudents: async (params?: { search?: string; subject_id?: string }): Promise<any[]> => {
        const qs = params
            ? '?' + Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`).join('&')
            : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/notices/students${qs}`);
        return data.data;
    },

    create: async (noticeData: {
        title: string;
        message: string;
        recipient_id?: string;
        recipient_ids?: string[];
        recipient_type?: string;
        from_date?: string;
        to_date?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/notices', noticeData);
        return data.data;
    },

    update: async (id: string, noticeData: {
        title?: string;
        message?: string;
        from_date?: string;
        to_date?: string;
    }): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/notices/${id}`, noticeData);
        return data.data;
    },

    markAsRead: async (id: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/notices/${id}/read`);
        return data.data;
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/notices/${id}`);
    },

    getUnreadCount: async (): Promise<number> => {
        const { data } = await apiClient.get<ApiResponse<{ count: number }>>('/api/notices/unread-count');
        return data.data.count;
    },
};

// ============================================
// Revenues API
// ============================================

export const revenuesApi = {
    list: async (params?: QueryParams): Promise<any> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/revenues${query}`);
        return data.data;
    },

    getSummary: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/revenues/summary');
        return data.data;
    },

    getAnalytics: async (params?: { institute_id?: string; start_date?: string; end_date?: string; enrollment_type?: string }): Promise<any> => {
        const qs = params ? '?' + Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v as string)}`).join('&') : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/revenues/analytics${qs}`);
        return data.data;
    },

    create: async (revenueData: {
        institute_id: string;
        amount: number;
        payment_method?: string;
        transaction_id?: string;
        description?: string;
        payment_date?: string;
        status?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/revenues', revenueData);
        return data.data;
    },

    update: async (id: string, revenueData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/revenues/${id}`, revenueData);
        return data.data;
    },

    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/revenues/${id}`);
    },

    // ── Receipt audit (read-only on payment records; writes to audit log only) ──

    getReceiptAudit: async (paymentId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/revenues/receipts/${paymentId}/audit`);
        return data.data ?? [];
    },

    auditReceiptAction: async (paymentId: string, action: 'flag_disputed' | 'unflag' | 'reconcile' | 'note', note: string, metadata?: Record<string, unknown>): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/revenues/receipts/${paymentId}/action`, { action, note, metadata });
        return data.data;
    },
};

// ============================================
// Finance API (Specialized Enrollments & Installments)
// ============================================

export const financeApi = {
    searchStudents: async (query: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/finance/students/search?q=${query}`);
        return data.data;
    },

    getEnrollments: async (studentId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/finance/enrollments/${studentId}`);
        return data.data;
    },

    createEnrollment: async (enrollmentData: {
        student_id: string;
        course_id?: string;
        subject?: string;
        total_amount: number;
        installments: { due_date: string; amount: number }[];
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/enrollments', enrollmentData);
        return data.data;
    },


    recordPayment: async (paymentData: {
        student_id: string;
        enrollment_id: string;
        amount: number;
        payment_method: string;
        reference?: string;
        description?: string;
        payment_date?: string;
        next_due_date?: string;
        /**
         * Generated once for the payment the operator intends to make, and
         * reused if the request has to be sent again.
         *
         * Without it the server can only guess from timing, and it guessed in
         * the direction that loses money: an identical payment within three
         * minutes was treated as a double-click and discarded, so a student
         * paying the same amount twice in one visit had the second collection
         * silently dropped — while a retry a few minutes later produced a
         * second payment and a second receipt. The key tells the two apart
         * exactly.
         */
        idempotency_key?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/payments', paymentData);
        return data.data;
    },

    // Razorpay order creation / verification always needs a real round-trip to
    // the payment gateway — there is no meaningful way to "queue this for later
    // sync" the way an ordinary write can be. forceRemote skips the exe's
    // local-first queueing so a fake order id never reaches the checkout widget.
    createRazorpayOrder: async (payload: {
        student_id: string;
        enrollment_id: string;
        amount: number;
        description?: string;
        payment_date?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/payments/razorpay/order', payload, { forceRemote: true } as any);
        return data.data;
    },

    verifyRazorpayPayment: async (payload: {
        student_id: string;
        enrollment_id: string;
        amount: number;
        description?: string;
        payment_date?: string;
        next_due_date?: string;
        razorpay_order_id: string;
        razorpay_payment_id: string;
        razorpay_signature: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/payments/razorpay/verify', payload, { forceRemote: true } as any);
        return data.data;
    },

    getStudentPayments: async (studentId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/finance/payments/student/${studentId}`);
        return data.data;
    },

    updatePayment: async (id: string, paymentData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/finance/payments/${id}`, paymentData);
        return data.data;
    },

    getPaymentAudit: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/finance/payments/${id}/audit`);
        return data.data;
    },

    addPaymentAudit: async (id: string, payload: { action: 'flag_disputed' | 'unflag' | 'reconcile' | 'note'; note?: string; metadata?: Record<string, unknown> }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/finance/payments/${id}/audit`, payload);
        return data.data;
    },

    deletePayment: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/finance/payments/${id}`);
    },

    getQRPayload: async (amount: number, enrollmentId: string): Promise<{ payload: string; upi_url?: string; upi_id?: string; amount?: number; display_name?: string; qr_image_url?: string | null }> => {
        const { data } = await apiClient.get<ApiResponse<{ payload: string; upi_url?: string; upi_id?: string; amount?: number; display_name?: string; qr_image_url?: string | null }>>(`/api/finance/qr-payload?amount=${amount}&enrollment_id=${enrollmentId}`);
        return data.data;
    },

    recordUpiAttempt: async (paymentData: {
        student_id: string;
        enrollment_id: string;
        amount: number;
        status: 'success' | 'pending' | 'failed';
        reference?: string;
        description?: string;
        payment_date?: string;
        next_due_date?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/payments/upi-attempt', paymentData);
        return data.data;
    },

    getRecentPayments: async (params?: { start_date?: string; end_date?: string; page?: number; per_page?: number; search?: string; payment_method?: string; enrollment_type?: string }): Promise<{ payments: any[]; pagination: { page: number; per_page: number; total: number; total_pages: number } }> => {
        const searchParams = new URLSearchParams();
        if (params?.enrollment_type) searchParams.set('enrollment_type', params.enrollment_type);
        if (params?.start_date) searchParams.set('start_date', params.start_date);
        if (params?.end_date) searchParams.set('end_date', params.end_date);
        if (params?.page) searchParams.set('page', String(params.page));
        if (params?.per_page) searchParams.set('per_page', String(params.per_page));
        if (params?.search) searchParams.set('search', params.search);
        if (params?.payment_method) searchParams.set('payment_method', params.payment_method);
        const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/finance/payments${qs}`);
        return data.data;
    },

    getAnalytics: async (params?: { start_date?: string; end_date?: string; enrollment_type?: string }): Promise<any> => {
        const searchParams = new URLSearchParams();
        if (params?.enrollment_type) searchParams.set('enrollment_type', params.enrollment_type);
        if (params?.start_date) searchParams.set('start_date', params.start_date);
        if (params?.end_date) searchParams.set('end_date', params.end_date);
        const qs = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/finance/analytics${qs}`);
        return data.data;
    },

    getSummary: async (params?: { start_date?: string; end_date?: string; enrollment_type?: string }): Promise<any> => {
        const searchParams = new URLSearchParams();
        if (params?.enrollment_type) searchParams.set('enrollment_type', params.enrollment_type);
        if (params?.start_date) searchParams.set('start_date', params.start_date);
        if (params?.end_date) searchParams.set('end_date', params.end_date);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/finance/summary${query}`);
        return data.data;
    },

    getInvoices: async (params?: { search?: string; method?: string; from_date?: string; to_date?: string; page?: number; per_page?: number; enrollment_type?: string }): Promise<{ data: any[]; pagination: any; summary: any }> => {
        const sp = new URLSearchParams();
        if (params?.enrollment_type) sp.set('enrollment_type', params.enrollment_type);
        if (params?.search)    sp.set('search',    params.search);
        if (params?.method && params.method !== 'all') sp.set('method', params.method);
        if (params?.from_date) sp.set('from_date', params.from_date);
        if (params?.to_date)   sp.set('to_date',   params.to_date);
        if (params?.page)      sp.set('page',      String(params.page));
        if (params?.per_page)  sp.set('per_page',  String(params.per_page));
        const q = sp.toString() ? `?${sp}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/finance/invoices${q}`);
        return { data: data.data, pagination: (data as any).pagination, summary: (data as any).summary };
    },

    getStudentFinancialSummary: async (studentId: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/finance/students/${studentId}/financial-summary`);
        return data.data;
    },

    getSubjectEnrollments: async (studentId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/finance/subject-enrollments/${studentId}`);
        return data.data;
    },

    recordSubjectPayment: async (payload: {
        student_id: string;
        subject_enrollment_id: string;
        enrollment_id: string;
        amount: number;
        payment_method: string;
        reference?: string;
        description?: string;
        payment_date?: string;
        next_due_date?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/subject-payments', payload);
        return data.data;
    },

    getInstallments: async (params?: {
        search?: string;
        mode?: string;
        course_id?: string;
        institute_id?: string;
        days_ahead?: number;
        enrollment_type?: string;
        page?: number;
        per_page?: number;
    }): Promise<{ data: any[]; pagination: any }> => {
        const q = params ? '?' + new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null).map(([k, v]) => [k, String(v)])
        ).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/finance/installments${q}`);
        return { data: data.data || [], pagination: (data as any).pagination || {} };
    },

    rescheduleInstallment: async (sseId: string, newDueDate: string, reason?: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/finance/installments/${sseId}/reschedule`, { new_due_date: newDueDate, reason });
        return data.data;
    },

    blockInstallment: async (sseId: string, reason?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/finance/installments/${sseId}/block`, { reason });
        return data.data;
    },

    getSseList: async (params?: {
        search?: string;
        status?: string;
        course_id?: string;
        institute_id?: string;
        enrollment_type?: string;
        page?: number;
        per_page?: number;
    }): Promise<{ data: any[]; pagination: any }> => {
        const q = params ? '?' + new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null).map(([k, v]) => [k, String(v)])
        ).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/finance/sse-list${q}`);
        return { data: data.data || [], pagination: (data as any).pagination || {} };
    },

    createSubjectRazorpayOrder: async (payload: {
        student_id: string;
        enrollment_id: string;
        subject_enrollment_id: string;
        amount: number;
        description?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/subject-payments/razorpay/order', payload, { forceRemote: true } as any);
        return data.data;
    },

    verifySubjectRazorpayPayment: async (payload: {
        student_id: string;
        enrollment_id: string;
        subject_enrollment_id: string;
        amount: number;
        description?: string;
        payment_date?: string;
        razorpay_order_id: string;
        razorpay_payment_id: string;
        razorpay_signature: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/subject-payments/razorpay/verify', payload, { forceRemote: true } as any);
        return data.data;
    },

    recordPaymentAttempt: async (payload: {
        student_id: string;
        enrollment_id?: string;
        subject_enrollment_id?: string;
        institute_id?: string;
        amount: number;
        payment_method: string;
        status: 'failed' | 'cancelled';
        reference?: string;
        description?: string;
        payment_date?: string;
        gateway_provider?: string;
        gateway_order_id?: string;
        gateway_payment_id?: string;
        gateway_signature?: string;
        failure_reason?: string;
        gateway_payload?: Record<string, unknown>;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/payment-attempts', payload);
        return data.data;
    },
};

// ============================================
// Syllabus API
// ============================================

export const syllabusApi = {
    // Sections
    getSections: async (subjectId?: string): Promise<any[]> => {
        const query = subjectId ? `?subject_id=${subjectId}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/syllabus/sections${query}`);
        return data.data;
    },
    createSection: async (sectionData: { subject_id: string; name: string; description?: string }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/syllabus/sections', sectionData);
        return data.data;
    },
    updateSection: async (id: string, sectionData: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/syllabus/sections/${id}`, sectionData);
        return data.data;
    },
    deleteSection: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/syllabus/sections/${id}`);
    },

    // Bulk Uploads
    uploadTyping: async (payload: { subject_id: string; section_id: string; questions: any[] }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/syllabus/upload/typing', payload);
        return data.data;
    },
    uploadMCQ: async (payload: { subject_id: string; section_id: string; language: string; questions: any[] }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/syllabus/upload/mcq', payload);
        return data.data;
    },

    // Question Management
    getTypingQuestions: async (sectionId: string, subjectId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/syllabus/questions/typing?section_id=${sectionId}&subject_id=${subjectId}`);
        return data.data;
    },
    getMCQQuestions: async (sectionId: string, subjectId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/syllabus/questions/mcq?section_id=${sectionId}&subject_id=${subjectId}`);
        return data.data;
    },
    deleteTypingQuestion: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/syllabus/questions/typing/${id}`);
    },
    deleteMCQQuestion: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/syllabus/questions/mcq/${id}`);
    },
};

// ============================================
// Exam Rules API
// ============================================

export const examRulesApi = {
    list: async (params?: { exam_type?: string; is_active?: boolean; exclude_bundle_members?: boolean; kind?: 'rule' | 'paper' }): Promise<any[]> => {
        const q = params ? '?' + new URLSearchParams(
            Object.entries(params).filter(([, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
        ).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/exam-rules${q}`);
        return data.data;
    },
    get: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/exam-rules/${id}`);
        return data.data;
    },
    create: async (payload: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/exam-rules', payload);
        return data.data;
    },
    update: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/exam-rules/${id}`, payload);
        return data.data;
    },
    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/exam-rules/${id}`);
    },
    comparatorCriteria: async (format?: string): Promise<any> => {
        const q = format ? `?format=${format}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/exam-rules/comparator-criteria${q}`);
        return data.data;
    },
};

// ============================================
// Practice Exam Papers API (bundled multi-exam-type papers)
// ============================================

export const practiceExamPapersApi = {
    /**
     * Build a subject's exam from the question types it actually has — one
     * section each, with that type's own marks and passing mark.
     *
     * Refuses rather than guessing: a subject with no question types, or a type
     * with no paper uploaded, comes back as an error naming the gap. An exam
     * quietly missing a section still looks like an exam and marks out of the
     * wrong total.
     */
    generateFromSubject: async (payload: {
        subject_id: string;
        name?: string;
        is_demo?: boolean;
        /** Preview the sections and totals without writing anything. */
        dry_run?: boolean;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/practice-exam-papers/generate-from-subject', payload);
        return data.data;
    },
    list: async (params?: { course_id?: string; subject_id?: string }): Promise<any[]> => {
        const q = params ? '?' + new URLSearchParams(
            Object.entries(params).filter(([, v]) => v) as [string, string][]
        ).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/practice-exam-papers${q}`);
        return data.data;
    },
    get: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/practice-exam-papers/${id}`);
        return data.data;
    },
    create: async (payload: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/practice-exam-papers', payload);
        return data.data;
    },
    update: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/practice-exam-papers/${id}`, payload);
        return data.data;
    },
    /**
     * Switch a paper on or off. Deactivating is what a list page wants where it
     * currently offers only Delete: an exam no longer in use still has results
     * attached to it.
     */
    setActive: async (id: string, is_active: boolean): Promise<any> => {
        const { data } = await apiClient.patch<ApiResponse<any>>(`/api/practice-exam-papers/${id}/active`, { is_active });
        return data.data;
    },
    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/practice-exam-papers/${id}`);
    },
};

// ============================================
// Guided Tutor — authoring, publishing, translating
//
// Until this existed NOTHING in the frontend called the tutor API at all: a
// reviewed, narrated Word course sat in the database as drafts, and the only
// way to publish it was an authenticated curl against production.
// ============================================

export const tutorApi = {
    /** Chapters with their counts, ownership and whether THIS institute hid them. */
    chapters: async (app?: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(
            `/api/tutor-admin/chapters${app ? `?app=${encodeURIComponent(app)}` : ''}`);
        return data.data;
    },
    lessons: async (chapterId?: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(
            `/api/tutor/lessons${chapterId ? `?chapter_id=${encodeURIComponent(chapterId)}` : ''}`);
        return data.data;
    },
    /** One lesson with every step and its audio — drafts included, unlike the student read. */
    lesson: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/tutor/lessons/${id}`);
        return data.data;
    },
    /**
     * The publish gate, run read-only. This is what lets the screen disable
     * Publish and name the offending step instead of letting the author
     * discover the problem by triggering a 400.
     */
    review: async (lessonId: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/tutor-admin/lessons/${lessonId}/review`);
        return data.data;
    },
    createChapter: async (payload: { app: string; title: string; description?: string | null; seq?: number }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/tutor/chapters', payload);
        return data.data;
    },
    updateChapter: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/tutor/chapters/${id}`, payload);
        return data.data;
    },
    deleteChapter: async (id: string): Promise<void> => { await apiClient.delete(`/api/tutor/chapters/${id}`); },
    createLesson: async (payload: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/tutor/lessons', payload);
        return data.data;
    },
    updateLesson: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/tutor/lessons/${id}`, payload);
        return data.data;
    },
    deleteLesson: async (id: string): Promise<void> => { await apiClient.delete(`/api/tutor/lessons/${id}`); },
    createStep: async (lessonId: string, payload: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/tutor/lessons/${lessonId}/steps`, payload);
        return data.data;
    },
    updateStep: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/tutor/steps/${id}`, payload);
        return data.data;
    },
    deleteStep: async (id: string): Promise<void> => { await apiClient.delete(`/api/tutor/steps/${id}`); },
    reorderSteps: async (lessonId: string, order: string[]): Promise<void> => {
        await apiClient.post(`/api/tutor/lessons/${lessonId}/steps/reorder`, { order });
    },
    publish: async (lessonId: string): Promise<void> => {
        await apiClient.post(`/api/tutor/lessons/${lessonId}/publish`);
    },
    unpublish: async (lessonId: string): Promise<void> => {
        await apiClient.post(`/api/tutor/lessons/${lessonId}/unpublish`);
    },
    /**
     * Hide a chapter from THIS institute's students, or bring it back. Not a
     * delete: platform content is one row every institute reads.
     */
    setHidden: async (chapterId: string, hidden: boolean): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/tutor-admin/chapters/${chapterId}/hidden`, { hidden });
        return data.data;
    },
    lessonCoverage: async (lessonId: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/tutor-translate/lessons/${lessonId}/coverage`);
        return data.data;
    },
    chapterCoverage: async (chapterId: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/tutor-translate/chapters/${chapterId}/coverage`);
        return data.data;
    },
    /** Returns the run's summary, `rejected` array included — the screen shows it. */
    translateLesson: async (lessonId: string, languages?: string[]): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/tutor-translate/lessons/${lessonId}`, { languages });
        return data.data;
    },
    translateChapter: async (chapterId: string, languages?: string[]): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/tutor-translate/chapters/${chapterId}`, { languages });
        return data.data;
    },
    /**
     * Every student's standing in the guided tutor, for the institute's own
     * staff. The response carries `scoreFormula` and `scoreNote` with the
     * numbers; the page prints them rather than describing the score itself,
     * so the explanation cannot drift from the arithmetic.
     */
    progressReport: async (params: { app?: string; student_id?: string; session_id?: string } = {}): Promise<any> => {
        const qs = new URLSearchParams();
        if (params.app) qs.set('app', params.app);
        if (params.student_id) qs.set('student_id', params.student_id);
        if (params.session_id) qs.set('session_id', params.session_id);
        const { data } = await apiClient.get<ApiResponse<any>>(
            `/api/tutor/progress-report${qs.toString() ? `?${qs}` : ''}`);
        return data.data;
    },
};

// ============================================
// Email Attachment Library (reusable attachments for Email Writing papers)
// ============================================

export const emailAttachmentsApi = {
    list: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/email-attachments');
        return data.data;
    },
    get: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/email-attachments/${id}`);
        return data.data;
    },
    create: async (payload: { name: string; file_name?: string; file_base64?: string | null; mime_type?: string | null; size_bytes?: number | null }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/email-attachments', payload);
        return data.data;
    },
    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/email-attachments/${id}`);
    },
};

// ============================================
// Typing Marking Rules API
// ============================================

export const markingRulesApi = {
    list: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/exam-rules/marking-rules');
        return data.data;
    },
    get: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/exam-rules/marking-rules/${id}`);
        return data.data;
    },
    create: async (payload: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/exam-rules/marking-rules', payload);
        return data.data;
    },
    update: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/exam-rules/marking-rules/${id}`, payload);
        return data.data;
    },
    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/exam-rules/marking-rules/${id}`);
    },
};

// ============================================
// Scheduled Exams (Assessments) API
// ============================================

export const scheduledExamsApi = {
    list: async (params?: { status?: string; exam_type?: string }): Promise<any[]> => {
        const q = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/assessments${q}`);
        return data.data;
    },
    get: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/assessments/${id}`);
        return data.data;
    },
    create: async (payload: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/assessments', payload);
        return data.data;
    },
    update: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/assessments/${id}`, payload);
        return data.data;
    },
    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/assessments/${id}`);
    },
    releaseResult: async (id: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/assessments/${id}/release-result`);
        return data.data;
    },
    getResults: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/assessments/${id}/results`);
        return data.data;
    },
};

// ============================================
// Unified API Export
// ============================================

export const api = {
    auth: authApi,
    users: usersApi,
    institutes: institutesApi,
    questions: questionsApi,
    rubrics: rubricsApi,
    testSessions: testSessionsApi,
    analytics: {
        student: analyticsApi.getStudentAnalytics,
        institute: analyticsApi.getInstituteAnalytics,
        export: analyticsApi.exportReport,
    },
    assignments: assignmentsApi,
    masterData: masterDataApi,
    notices: noticesApi,
    revenues: revenuesApi,
    finance: financeApi,
    syllabus: syllabusApi,
};



// ============================================
// Institute Admin API (Admin.pdf features)
// ============================================

export const instituteAdminApi = {
    // Allotted Sessions (read-only; managed by super admin)
    getTimings: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/sessions');
        return data.data;
    },

    // Session courses — courses an institute has activated per allotted session
    getSessionCourses: async (sessionId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/sessions/${sessionId}/courses`);
        return data.data;
    },
    addSessionCourse: async (sessionId: string, courseId: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/sessions/${sessionId}/courses`, { course_id: courseId });
        return data.data;
    },
    removeSessionCourse: async (sessionId: string, courseId: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/sessions/${sessionId}/courses/${courseId}`);
    },

    // Institute → Super Admin student payments
    getInstitutePayments: async (params?: { status?: string; session_id?: string }): Promise<any[]> => {
        const q = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/institute-payments${q}`);
        return data.data;
    },
    submitInstitutePayment: async (payload: {
        student_id: string;
        session_allotment_id: string;
        amount?: number;
        payment_method?: string;
        transaction_ref?: string;
        payment_date?: string;
        notes?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/institute-payments', payload);
        return data.data;
    },
    cancelInstitutePayment: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/institute-payments/${id}`);
    },

    // Practice Slots
    getPracticeSlots: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/practice-slots');
        return data.data;
    },
    createPracticeSlot: async (slot: {
        slot_name: string;
        start_time: string;
        end_time: string;
        duration_minutes: number;
        auto_logout?: boolean;
        max_students?: number;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/practice-slots', slot);
        return data.data;
    },
    updatePracticeSlot: async (id: string, slot: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/practice-slots/${id}`, slot);
        return data.data;
    },
    deletePracticeSlot: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/practice-slots/${id}`);
    },

    // Typing Lessons
    getTypingLessons: async (params?: { subject_id?: string; practice_type_id?: string }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.subject_id) searchParams.set('subject_id', params.subject_id);
        if (params?.practice_type_id) searchParams.set('practice_type_id', params.practice_type_id);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/typing-lessons${query}`);
        return data.data;
    },
    createTypingLesson: async (payload: {
        subject_id: string;
        practice_type_id?: string;
        category: string;
        lesson_name: string;
        lesson_number: number;
        lesson_content: string;
        language: string;
        wpm_level: number;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/typing-lessons', payload);
        return data.data;
    },
    updateTypingLesson: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/typing-lessons/${id}`, payload);
        return data.data;
    },
    deleteTypingLesson: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/typing-lessons/${id}`);
    },

    // Theory Tests
    getTheoryTests: async (params?: { subject_id?: string }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.subject_id) searchParams.set('subject_id', params.subject_id);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/theory-tests${query}`);
        return data.data;
    },
    createTheoryTest: async (payload: {
        subject_id: string;
        category: string;
        question: string;
        option_a: string;
        option_b: string;
        option_c: string;
        option_d: string;
        correct_answer: string;
        difficulty?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/theory-tests', payload);
        return data.data;
    },
    updateTheoryTest: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/theory-tests/${id}`, payload);
        return data.data;
    },
    deleteTheoryTest: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/theory-tests/${id}`);
    },

    /**
     * Upload an actual file (video / audio / PDF) and get back the URL to use.
     *
     * Before this, course media could only be LINKED: every storage provider in
     * the settings is unconfigured on every institute and the default is
     * url_only, so an institute holding an .mp4 had to publish it somewhere
     * else first. The returned url is a path on this server
     * ("/api/media/<id>"), not an absolute URL -- so any field it is pasted
     * into must accept a path, not just type="url".
     *
     * onProgress reports 0-100 while the bytes go up. A lesson recording takes
     * long enough that a form with no feedback reads as a frozen page.
     */
    uploadMedia: async (
        file: File,
        opts: { isPlatform?: boolean; onProgress?: (percent: number) => void } = {}
    ): Promise<{ id: string; url: string; size_bytes: number; mime_type: string; original_name: string }> => {
        const form = new FormData();
        form.append('file', file);
        if (opts.isPlatform) form.append('is_platform', 'true');
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/media/upload', form, {
            onUploadProgress: (event) => {
                if (!opts.onProgress || !event.total) return;
                opts.onProgress(Math.round((event.loaded / event.total) * 100));
            },
        });
        return data.data;
    },
    getMediaUploads: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/media');
        return data.data;
    },
    deleteMediaUpload: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/media/${id}`);
    },

    // Learning Resources
    getVideoTutorials: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/video-tutorials');
        return data.data;
    },
    createVideoTutorial: async (payload: {
        title: string;
        description?: string;
        video_url: string;
        thumbnail_url?: string;
        duration_minutes?: number;
        subject_id?: string | null;
        sort_order?: number;
        is_active?: boolean;
        /**
         * Publish to EVERY institute, stored as institute_id NULL — which is how
         * all platform content is scoped and how its checkpoint questions travel
         * with it. Honoured for a super_admin only; ignored for anyone else, so
         * an institute cannot publish its material to the whole platform.
         */
        is_platform?: boolean;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/video-tutorials', payload);
        return data.data;
    },
    updateVideoTutorial: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/video-tutorials/${id}`, payload);
        return data.data;
    },
    deleteVideoTutorial: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/video-tutorials/${id}`);
    },

    // ── Checkpoint questions inside a video ──────────────────────────────
    // What turns a tutorial into something a student takes part in: playback
    // pauses at a timestamp and asks a question before it will go on.
    getVideoCheckpoints: async (videoId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/video-tutorials/${videoId}/checkpoints`);
        return data.data;
    },
    // The whole list, replacing whatever was there. One request rather than per-row
    // CRUD because that is how the editor works -- and because it is atomic, so a
    // dropped connection leaves the video with the checkpoints it had rather than
    // half of the ones it was getting.
    saveVideoCheckpoints: async (videoId: string, checkpoints: Array<{
        at_seconds: number;
        question: string;
        options: string[];
        correct_index?: number | null;
        explanation?: string;
        is_active?: boolean;
        sort_order?: number;
    }>): Promise<any[]> => {
        const { data } = await apiClient.put<ApiResponse<any[]>>(
            `/api/institute-admin/video-tutorials/${videoId}/checkpoints`,
            { checkpoints },
        );
        return data.data;
    },
    getVideoCheckpointResults: async (videoId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/video-tutorials/${videoId}/checkpoint-results`);
        return data.data;
    },
    getEbooks: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/ebooks');
        return data.data;
    },
    createEbook: async (payload: {
        title: string;
        description?: string;
        file_url: string;
        thumbnail_url?: string;
        subject_id?: string | null;
        is_active?: boolean;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/ebooks', payload);
        return data.data;
    },
    updateEbook: async (id: string, payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/ebooks/${id}`, payload);
        return data.data;
    },
    deleteEbook: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/ebooks/${id}`);
    },

    // Subject Fees (per-institute override of what students pay per subject)
    getSubjectFees: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/subject-fees');
        return data.data || [];
    },
    // Only provided keys are sent. repeaterFee null clears the repeater override.
    updateSubjectFee: async (subjectId: string, fee?: number, notes?: string, repeaterFee?: number | null): Promise<any> => {
        const body: Record<string, unknown> = {};
        if (fee !== undefined) body.fee = fee;
        if (notes !== undefined) body.notes = notes;
        if (repeaterFee !== undefined) body.repeater_fee = repeaterFee;
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/subject-fees/${subjectId}`, body);
        return data.data;
    },

    // Fee Structures
    getFeeStructures: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/fee-structures');
        return data.data;
    },
    createFeeStructure: async (fee: {
        course_id?: string;
        subject_id?: string;
        student_type: 'fresher' | 'repeater';
        fee_name: string;
        amount: number;
        frequency?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/fee-structures', fee);
        return data.data;
    },
    updateFeeStructure: async (id: string, fee: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/fee-structures/${id}`, fee);
        return data.data;
    },
    deleteFeeStructure: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/fee-structures/${id}`);
    },

    // Batches
    getBatches: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/batches');
        return data.data;
    },
    createBatch: async (batch: {
        batch_name: string;
        start_time: string;
        end_time: string;
        max_students?: number;
        is_active?: boolean;
        description?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/batches', batch);
        return data.data;
    },
    updateBatch: async (id: string, batch: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/batches/${id}`, batch);
        return data.data;
    },
    deleteBatch: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/batches/${id}`);
    },

    // Student Register (operations-grade list with financial summary)
    getStudentRegister: async (params?: QueryParams): Promise<PaginatedResponse<any>> => {
        const qs = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<any>>>(`/api/institute-admin/student-register${qs}`);
        return data.data;
    },

    // Student Profiles
    getStudentProfiles: async (params?: QueryParams): Promise<PaginatedResponse<any>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<any>>>(`/api/institute-admin/student-profiles${query}`);
        return data.data;
    },
    getStudentProfile: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/student-profiles/${id}`);
        return data.data;
    },
    getStudentResults: async (id: string): Promise<{ typing_results: any[]; theory_results: any[]; client_exam_sessions: any[] }> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/student-profiles/${id}/results`);
        return data.data;
    },
    getStudentProfileResolved: async (id: string): Promise<any> => {
        try {
            return await instituteAdminApi.getStudentProfile(id);
        } catch (error: any) {
            const status = error?.response?.status;
            // For 403 (forbidden), don't try fallback — propagate immediately
            if (status === 403) throw error;

            // For 404 or 400 (bad UUID format) or 500 (enrichment error),
            // try searching all profiles as a fallback
            try {
                const profiles = await instituteAdminApi.getStudentProfiles({ per_page: 500 });
                const rows = profiles?.data || [];
                const matchedProfile = rows.find((profile: any) =>
                    profile?.id === id ||
                    profile?.user_id === id ||
                    profile?.user?.id === id
                );

                if (!matchedProfile) throw error;

                // If the match is by user_id but profile has its own ID, fetch the full profile
                if (matchedProfile.id && matchedProfile.id !== id) {
                    try {
                        return await instituteAdminApi.getStudentProfile(matchedProfile.id);
                    } catch {
                        return matchedProfile;
                    }
                }
                return matchedProfile;
            } catch (fallbackError: any) {
                if (fallbackError === error) throw error;
                throw error;
            }
        }
    },
    createStudentProfile: async (profile: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/student-profiles', profile);
        return data.data;
    },
    updateStudentProfile: async (id: string, profile: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/student-profiles/${id}`, profile);
        return data.data;
    },
    // Status-change actions on an EXISTING row (archive/unblock/lifecycle) are
    // forceRemote: the exe's local-first write path only knows how to append
    // or field-merge a row by id, it doesn't know how to reach into a nested
    // subject_enrollments array or flip is_active — patching it "optimistically"
    // risked leaving the UI showing stale state until the next sync while
    // claiming success now. A real round-trip guarantees the shown result is
    // the actual result.
    archiveStudentProfile: async (id: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/student-profiles/${id}/archive`, undefined, { forceRemote: true } as any);
        return data.data;
    },

    enrollStudentInCourse: async (profileId: string, courseId: string, sessionId?: string, subjectIds?: string[], subjectBatches?: Record<string, string | string[]>, enrollmentType?: 'regular' | 'repeater'): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/student-profiles/${profileId}/enroll`, {
            course_id: courseId,
            enrollment_type: enrollmentType || undefined,
            session_id: sessionId || undefined,
            subject_ids: subjectIds?.length ? subjectIds : undefined,
            subject_batches: subjectBatches && Object.keys(subjectBatches).length > 0 ? subjectBatches : undefined,
        });
        return data;
    },

    enrollStudentInCourseOnly: async (profileId: string, courseId: string, sessionId?: string): Promise<{ enrollment_id: string; course_id: string; course_name: string; fee: number; student_name: string; gr_number: string | null; date: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/student-profiles/${profileId}/enroll-course`, {
            course_id: courseId,
            session_id: sessionId || undefined,
        });
        return data.data;
    },

    enrollStudentInSubjectItem: async (profileId: string, payload: { enrollment_id: string; subject_id: string; course_id: string; enrollment_type?: 'regular' | 'repeater' }): Promise<{ sse_id: string; subject_id: string; subject_name: string; fee: number; enrollment_id: string; enrollment_type?: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/student-profiles/${profileId}/enroll-subject-item`, payload);
        return data.data;
    },

    unenrollFromCourse: async (profileId: string, enrollmentId: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/student-profiles/${profileId}/unenroll-course/${enrollmentId}`);
    },

    unenrollFromSubject: async (profileId: string, sseId: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/student-profiles/${profileId}/unenroll-subject/${sseId}`);
    },

    unblockSubject: async (profileId: string, sseId: string, payload?: { remark?: string; grace_days?: number }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/student-profiles/${profileId}/unblock-subject/${sseId}`, payload || {}, { forceRemote: true } as any);
        return data.data;
    },

    // Student Payments/Installments
    getStudentPayments: async (params?: string | (QueryParams & { student_id?: string; start_date?: string; end_date?: string; status?: string })): Promise<PaginatedResponse<any>> => {
        const normalizedParams = typeof params === 'string' ? { student_id: params } : params;
        const query = buildQueryString(normalizedParams);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<any>>>(`/api/institute-admin/student-payments${query}`);
        return data.data;
    },

    getSubjectFeeCollections: async (params?: { enrollment_type?: string }): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/subject-fee-collections', { params: params?.enrollment_type ? { enrollment_type: params.enrollment_type } : undefined });
        return data.data;
    },
    getStudentEnrollmentStatus: async (params?: { status?: string; start_date?: string; end_date?: string; search?: string; page?: number; per_page?: number }): Promise<PaginatedResponse<any>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<any>>>(`/api/institute-admin/student-enrollment-status${query}`);
        return data.data;
    },
    // DB-driven role catalog (role_definitions) — use for role dropdowns
    // instead of hardcoded lists so new/renamed roles show up everywhere.
    getRoles: async (portal?: 'institute' | 'super_admin' | 'student'): Promise<Array<{
        role_key: string;
        role_name: string;
        summary: string;
        portal: string;
        locked: boolean;
        is_active: boolean;
        sort_order: number;
    }>> => {
        const query = portal ? `?portal=${portal}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/meta/roles${query}`);
        return data.data || [];
    },
    createStudentPayment: async (payment: {
        student_id: string;
        amount: number;
        payment_method?: string;
        fee_structure_id?: string;
        description?: string;
        installment_number?: number;
        total_installments?: number;
        enrollment_id?: string;
        course_id?: string;
        payment_date?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/student-payments', payment);
        return data.data;
    },
    getPaymentReceipt: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/student-payments/${id}/receipt`);
        return data.data;
    },
    deleteStudentPayment: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/student-payments/${id}`);
    },

    // Institute Settings
    getSystemConfig: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/settings/config');
        return data.data;
    },
    updateSystemConfig: async (payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>('/api/institute-admin/settings/config', payload);
        return data.data;
    },
    testGeminiTranslation: async (): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/integrations/mcq-translation/test');
        return data.data;
    },
    listGeminiModels: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/integrations/mcq-translation/models');
        return data.data;
    },
    getAccessPermissions: async (instituteId?: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/settings/access-permissions', {
            params: instituteId ? { institute_id: instituteId } : undefined,
        });
        return data.data;
    },
    updateAccessPermissions: async (payload: any, instituteId?: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>('/api/institute-admin/settings/access-permissions', payload, {
            params: instituteId ? { institute_id: instituteId } : undefined,
        });
        return data.data;
    },
    getPlatformLanguage: async (instituteId?: string): Promise<{ platform_language: string }> => {
        const { data } = await apiClient.get<ApiResponse<{ platform_language: string }>>('/api/institute-admin/settings/platform-language', {
            params: instituteId ? { institute_id: instituteId } : undefined,
        });
        return data.data;
    },
    updatePlatformLanguage: async (language: string): Promise<{ platform_language: string }> => {
        const { data } = await apiClient.put<ApiResponse<{ platform_language: string }>>('/api/institute-admin/settings/platform-language', { language });
        return data.data;
    },
    getPaymentGatewaySettings: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/settings/payment-gateway');
        return data.data;
    },
    updatePaymentGatewaySettings: async (payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>('/api/institute-admin/settings/payment-gateway', payload);
        return data.data;
    },
    getReceiptSeries: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/settings/receipt-series');
        return data.data;
    },
    updateReceiptSeries: async (payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>('/api/institute-admin/settings/receipt-series', payload);
        return data.data;
    },
    renumberReceipts: async (startAt = 1): Promise<{ renumbered: number; next_number: number }> => {
        const { data } = await apiClient.post<ApiResponse<{ renumbered: number; next_number: number }>>('/api/institute-admin/settings/receipt-series/renumber', { start_at: startAt });
        return data.data;
    },
    testEmailDelivery: async (to: string): Promise<{ message: string; method: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/settings/email/test', { to });
        return data.data;
    },

    // License Fee Ledger (what institute owes to platform)
    getLicenseFeeLedger: async (status?: string, params?: { enrollment_type?: string }): Promise<any> => {
        const sp = new URLSearchParams();
        if (status && status !== 'all') sp.set('status', status);
        if (params?.enrollment_type) sp.set('enrollment_type', params.enrollment_type);
        const q = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/license-fee-ledger${q}`);
        return data;
    },
    recordCourseFeeCashPayment: async (payload: { enrollment_id: string; student_id: string; amount: number; description?: string; payment_date?: string }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/finance/payments', {
            ...payload,
            payment_method: 'cash',
        });
        return data.data;
    },

    createStorageUploadUrl: async (payload: {
        file_name: string;
        content_type: string;
        folder?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/settings/integrations/storage/presign', payload);
        return data.data;
    },

    // Upload a content file's bytes straight into our cloud DB (no S3/provider
    // needed). Returns { id, url } where url = /api/content-files/:id served by
    // our own server. 403 CONTENT_UPLOAD_DISABLED when the super admin hasn't
    // enabled uploads for this institute.
    // `localOnly` keeps the file on the institute's own server: it is written to
    // the Institute EXE's encrypted resource cache and never uploaded. That path
    // must NOT force a remote call — forcing one is what makes the cloud upload
    // honest, and is exactly what this option is avoiding.
    uploadContentFileToDb: async (file: File, opts?: { localOnly?: boolean }): Promise<{ id: string; url: string; file_name: string }> => {
        const dataUrl: string = await new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onerror = () => reject(new Error('Could not read the file.'));
            r.onloadend = () => resolve(String(r.result || ''));
            r.readAsDataURL(file);
        });
        const localOnly = opts?.localOnly === true;
        const { data } = await apiClient.post<ApiResponse<{ id: string; url: string; file_name: string }>>(
            '/api/institute-admin/content-files',
            {
                file_name: file.name,
                mime_type: file.type || 'application/octet-stream',
                data_base64: dataUrl,
                ...(localOnly ? { local_only: true } : {}),
            },
            // Content files are stored centrally in the cloud DB (never the EXE), so
            // in the Institute EXE this must hit the cloud directly — online required.
            (localOnly ? {} : { forceRemote: true }) as any,
        );
        return data.data;
    },

    // ── Multi-provider content storage: OAuth "connect your own account" ──────
    // Platform OAuth app credentials (super-admin only; secrets masked in the GET).
    getStorageOAuthApps: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/integrations/storage/oauth-apps');
        return data.data;
    },
    updateStorageOAuthApps: async (payload: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>('/api/institute-admin/integrations/storage/oauth-apps', payload);
        return data.data;
    },
    // Returns { url } to open in a popup/redirect, or throws with an error code
    // (OAUTH_APP_NOT_CONFIGURED) the caller can surface.
    getStorageAuthorizeUrl: async (provider: string): Promise<{ url: string }> => {
        const { data } = await apiClient.get<ApiResponse<{ url: string }>>(`/api/institute-admin/integrations/storage/${provider}/authorize`);
        return data.data;
    },
    disconnectStorageProvider: async (provider: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/integrations/storage/${provider}/disconnect`);
        return data.data;
    },
    listStorageProviderFiles: async (provider: string, folder?: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/integrations/storage/${provider}/files`, {
            params: folder ? { folder } : undefined,
        });
        return data.data;
    },
    registerContentFile: async (payload: {
        provider: string;
        external_file_id: string;
        name: string;
        mime_type?: string;
        size_bytes?: number;
        checksum?: string;
        web_view_link?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/content-files/register', payload);
        return data.data;
    },
    listContentFiles: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/content-files');
        return data.data;
    },

    // Student Report (with chart stats + server-side pagination)
    getStudentReport: async (filters: {
        institute_id?: string;
        course_id?: string;
        subject_id?: string;
        session_id?: string;
        status?: string;
        admission_from?: string;
        admission_to?: string;
        search?: string;
        enrollment_type?: string;
        page?: number;
        per_page?: number;
    }): Promise<any> => {
        const params = Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined && v !== null)
        );
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/student-report', { params });
        return data.data;
    },

    // Live Students / Attendance
    getLiveStudents: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/live-students');
        return data.data;
    },
    checkInStudent: async (studentId: string, practiceSlotId?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/attendance/check-in', {
            student_id: studentId,
            practice_slot_id: practiceSlotId,
        });
        return data.data;
    },
    checkOutStudent: async (attendanceId: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/attendance/check-out/${attendanceId}`);
        return data.data;
    },
    // Check out by STUDENT id (closes today's open row) — robust for the Live
    // Presence "Out" button, whose row id may be a composite local-relay id
    // (PC-04-<uuid>) that the UUID-keyed check-out endpoint can't match. The
    // relay also intercepts this to clear the live PC session immediately.
    checkOutStudentByStudent: async (studentId: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/attendance/check-out-by-student', {
            student_id: studentId,
        });
        return data.data;
    },
    extendStudentTime: async (attendanceId: string, minutes: number): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/attendance/extend-time/${attendanceId}`, {
            extended_minutes: minutes,
        });
        return data.data;
    },
    updateStudentStatus: async (attendanceId: string, status: 'active' | 'practicing'): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/attendance/${attendanceId}/status`, { status });
        return data.data;
    },
    getAttendanceReport: async (params?: { start_date?: string; end_date?: string; session_id?: string; institute_id?: string; student_id?: string }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.start_date) searchParams.set('start_date', params.start_date);
        if (params?.end_date) searchParams.set('end_date', params.end_date);
        if (params?.session_id) searchParams.set('session_id', params.session_id);
        if (params?.institute_id) searchParams.set('institute_id', params.institute_id);
        if (params?.student_id) searchParams.set('student_id', params.student_id);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/attendance-report${query}`);
        return data.data;
    },
    getAttendanceSummary: async (params?: {
        start_date?: string;
        end_date?: string;
        batch_id?: string;
        course_id?: string;
        search?: string;
    }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.start_date) searchParams.set('start_date', params.start_date);
        if (params?.end_date) searchParams.set('end_date', params.end_date);
        if (params?.batch_id) searchParams.set('batch_id', params.batch_id);
        if (params?.course_id) searchParams.set('course_id', params.course_id);
        if (params?.search) searchParams.set('search', params.search);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/attendance-summary${query}`);
        return data.data;
    },

    // Lab PC Management
    getRegisteredPCs: async (): Promise<{ pcs: any[]; pc_limit: number | null; registered_count: number }> => {
        const { data } = await apiClient.get<any>('/api/institute-admin/lab/registered-pcs');
        return {
            pcs: data.data ?? [],
            pc_limit: data.meta?.pc_limit ?? null,
            registered_count: data.meta?.registered_count ?? (data.data?.length ?? 0),
        };
    },
    getUnregisteredDevices: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/lab/unregistered-devices');
        return data.data;
    },
    getPerformanceMonitor: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/lab/performance-monitor');
        return Array.isArray(data.data) ? data.data : [];
    },
    registerPC: async (payload: { pc_label: string; pc_number?: number; mac_address?: string; notes?: string }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/lab/registered-pcs', payload);
        return data.data;
    },
    updateRegisteredPC: async (id: string, payload: { pc_label?: string; pc_number?: number; mac_address?: string | null; notes?: string }): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/lab/registered-pcs/${id}`, payload);
        return data.data;
    },
    deleteRegisteredPC: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/lab/registered-pcs/${id}`);
    },
    generatePCKey: async (id: string): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/lab/registered-pcs/${id}/generate-key`, {});
        return data.data;
    },

    // Device / EXE license summary (read-only)
    getDeviceLicenseSummary: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/device-license/summary');
        return data.data;
    },

    // EXE sync
    getExeSyncBundle: async (since?: string): Promise<any> => {
        const q = since ? `?since=${encodeURIComponent(since)}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/exe/sync-bundle${q}`);
        return data.data;
    },
    getExeSyncLog: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/exe/sync-log');
        return data.data ?? [];
    },
    getExeFlowAudit: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/exe/flow-audit');
        return data.data;
    },

    // General Register
    getGeneralRegister: async (params?: QueryParams & { session_id?: string }): Promise<PaginatedResponse<any>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<any>>>(`/api/institute-admin/general-register${query}`);
        return data.data;
    },
    getAssignedCourses: async (): Promise<Array<{ id: string; name: string }>> => {
        const { data } = await apiClient.get<ApiResponse<Array<{ id: string; name: string }>>>('/api/institute-admin/assigned-courses');
        return data.data ?? [];
    },

    // ── Institute Course Management ──────────────────────────────────────────
    getInstituteCourses: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/courses');
        return data.data ?? [];
    },
    getAvailableMasterCourses: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/courses/available');
        return data.data ?? [];
    },
    // Master Data (institute-scoped) — subjects/exam-types for adopted courses only
    getMasterSubjects: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/master-data/subjects');
        return data.data ?? [];
    },
    getMasterExamTypes: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/master-data/exam-types');
        return data.data ?? [];
    },
    adoptMasterCourse: async (masterCourseId: string, overrideFee?: number): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/courses/adopt', {
            master_course_id: masterCourseId,
            override_fee: overrideFee ?? null,
        });
        return data.data;
    },
    updateInstituteCourse: async (courseId: string, updates: { override_fee?: number | null; name?: string; description?: string }): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/courses/${courseId}`, updates);
        return data.data;
    },
    removeInstituteCourse: async (courseId: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/courses/${courseId}`);
    },
    createInstituteCustomCourse: async (course: {
        name: string; description?: string;
        subjects: { name: string; description?: string; license_fee?: number }[];
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/courses/custom', course);
        return data.data;
    },

    // ── Batch Assignment ─────────────────────────────────────────────────────
    assignStudentBatch: async (profileId: string, batchId: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(
            `/api/institute-admin/student-profiles/${profileId}/assign-batch`,
            { batch_id: batchId }
        );
        return data.data;
    },
    bulkAssignBatch: async (profileIds: string[], batchId: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/batches/bulk-assign', {
            profile_ids: profileIds,
            batch_id: batchId,
        });
        return data.data;
    },

    createGREntry: async (entry: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/general-register', entry);
        return data.data;
    },
    updateGREntry: async (id: string, entry: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/general-register/${id}`, entry);
        return data.data;
    },
    exportGeneralRegister: async (mode: 'student_wise' | 'subject_wise' = 'student_wise'): Promise<Blob> => {
        const { data } = await apiClient.get(`/api/institute-admin/general-register/export?mode=${mode}`, {
            responseType: 'blob',
        });
        return data;
    },
    // Per-subject General Register — one auto-allocated GR number per subject enrollment.
    getSubjectRegister: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/general-register/subjects');
        return data.data ?? [];
    },
    exportSubjectRegister: async (view: 'student_wise' | 'subject_wise' = 'student_wise'): Promise<Blob> => {
        const { data } = await apiClient.get(`/api/institute-admin/general-register/subjects/export?view=${view}`, {
            responseType: 'blob',
        });
        return data;
    },

    // Staff Profiles
    getStaffProfiles: async (params?: { scope?: 'current' | 'all'; institute_id?: string }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.scope) searchParams.set('scope', params.scope);
        if (params?.institute_id) searchParams.set('institute_id', params.institute_id);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/staff-profiles${query}`);
        return data.data;
    },
    getStaffProfile: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/staff-profiles/${id}`);
        return data.data;
    },
    createStaffProfile: async (profile: any): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/staff-profiles', profile);
        return data.data;
    },
    updateStaffProfile: async (id: string, profile: any): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/staff-profiles/${id}`, profile);
        return data.data;
    },

    // Staff Report (server-side paginated with chart stats)
    getStaffReport: async (filters: {
        institute_id?: string;
        staff_type?: string;
        status?: string;
        specialization?: string;
        joining_from?: string;
        joining_to?: string;
        search?: string;
        page?: number;
        per_page?: number;
    }): Promise<any> => {
        const params = Object.fromEntries(
            Object.entries(filters).filter(([, v]) => v !== '' && v !== undefined && v !== null)
        );
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/staff-report', { params });
        return data.data;
    },

    // Institute student performance analytics
    getPerformanceAnalytics: async (params?: { session_id?: string; enrollment_type?: string }): Promise<any> => {
        const sp = new URLSearchParams();
        if (params?.session_id) sp.set('session_id', params.session_id);
        if (params?.enrollment_type) sp.set('enrollment_type', params.enrollment_type);
        const qs = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/analytics/performance${qs}`);
        return data.data;
    },

    // Staff lifecycle status change
    staffLifecycle: async (profileId: string, action: 'activate' | 'deactivate' | 'on_leave' | 'terminate', notes?: string, leaving_date?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(
            `/api/institute-admin/staff-profiles/${profileId}/lifecycle`,
            { action, notes, leaving_date },
            { forceRemote: true } as any
        );
        return data.data;
    },

    // Permanently delete a staff record (only allowed once terminated)
    deleteStaffProfile: async (profileId: string): Promise<any> => {
        const { data } = await apiClient.delete<ApiResponse<any>>(`/api/institute-admin/staff-profiles/${profileId}`);
        return data.data;
    },

    // Staff profile analytics (payment history + institute context)
    getStaffAnalytics: async (profileId: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/staff-profiles/${profileId}/analytics`);
        return data.data;
    },

    // Staff Payments
    getStaffPayments: async (params?: QueryParams & { staff_id?: string }): Promise<PaginatedResponse<any>> => {
        const query = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<PaginatedResponse<any>>>(`/api/institute-admin/staff-payments${query}`);
        return data.data;
    },
    createStaffPayment: async (payment: {
        staff_id: string;
        amount: number;
        net_amount: number;
        payment_method?: string;
        payment_type?: string;
        payment_for_month?: string;
        deductions?: number;
        description?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/staff-payments', payment);
        return data.data;
    },

    // Subject enrollment admin endpoints
    getBlockedSubjectEnrollments: async (params?: { institute_id?: string }): Promise<any[]> => {
        const q = params ? '?' + new URLSearchParams(Object.entries(params).filter(([, v]) => v) as [string, string][]).toString() : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/blocked-subject-enrollments${q}`);
        return data.data;
    },
    reactivateSubjectEnrollment: async (id: string, remark: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/subject-enrollments/${id}/reactivate`, { remark });
        return data.data;
    },
    enforceGracePeriod: async (instituteId?: string): Promise<{ blocked: number }> => {
        const { data } = await apiClient.post<ApiResponse<{ blocked: number }>>('/api/institute-admin/subject-enrollments/enforce-grace', instituteId ? { institute_id: instituteId } : {});
        return data.data;
    },

    // Dashboard Stats
    getDashboardStats: async (params?: {
        trend?: 'month' | 'year' | 'session';
        year?: number;
        session_id?: string;
        enrollment_type?: string;
    }): Promise<{
        active_students: number;
        total_students: number;
        live_students_count: number;
        today_revenue: number;
        active_sessions: any[];
        regular_students?: number;
        repeater_students?: number;
        total_instructors?: number;
        total_admission?: number;
        total_enrolled?: number;
        pending_fees?: number;
        collections_total?: number;
        collections_today?: number;
        collections_this_month?: number;
        collections_receipts_count?: number;
        license_total_owed?: number;
        license_total_paid?: number;
        license_total_pending?: number;
        blocked_students_count?: number;
        active_pcs_count?: number;
        recent_collection_receipts?: any[];
        recent_failed_license_attempts?: any[];
        blocked_reasons?: Array<{ reason: string; count: number }>;
        performance_summary?: { total_tests: number; average_score: number };
        trend?: 'month' | 'year' | 'session';
        trend_year?: number;
        monthly_revenue?: Array<{ name: string; month: string; value: number; revenue: number }>;
        pending_fee_students?: Array<{
            enrollment_id: string;
            balance: number;
            due_date?: string | null;
            subject?: string;
            student: { id: string; gr_number?: string; name: string; email?: string };
        }>;
    }> => {
        const searchParams = new URLSearchParams();
        if (params?.trend) searchParams.set('trend', params.trend);
        if (params?.year) searchParams.set('year', String(params.year));
        if (params?.session_id) searchParams.set('session_id', params.session_id);
        if (params?.enrollment_type) searchParams.set('enrollment_type', params.enrollment_type);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/dashboard-stats${query}`);
        return data.data;
    },

    getFinanceAnalytics: async (params?: { start_date?: string; end_date?: string; enrollment_type?: string }): Promise<any> => {
        const sp = new URLSearchParams();
        if (params?.enrollment_type) sp.set('enrollment_type', params.enrollment_type);
        if (params?.start_date) sp.set('start_date', params.start_date);
        if (params?.end_date)   sp.set('end_date',   params.end_date);
        const qs = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/finance-analytics${qs}`);
        return data.data;
    },

    getStudentPaymentReceiptAudit: async (paymentId: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/student-payments/${paymentId}/receipt/audit`);
        return data.data;
    },

    createStudentPaymentReceiptAudit: async (
        paymentId: string,
        payload: { action: 'flag_disputed' | 'unflag' | 'reconcile' | 'note'; note?: string; metadata?: Record<string, unknown> }
    ): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/student-payments/${paymentId}/receipt/audit`, payload);
        return data.data;
    },

    getDocumentTemplates: async (): Promise<Record<string, any>> => {
        const { data } = await apiClient.get<ApiResponse<Record<string, any>>>('/api/institute-admin/settings/document-templates');
        return data.data;
    },

    saveDocumentTemplate: async (key: string, config: Record<string, any>): Promise<Record<string, any>> => {
        const { data } = await apiClient.put<ApiResponse<Record<string, any>>>('/api/institute-admin/settings/document-templates', { [key]: config });
        return data.data;
    },

    getApprovalRequests: async (params?: { status?: string; action_key?: string; page?: number; per_page?: number }): Promise<any> => {
        const sp = new URLSearchParams();
        if (params?.status)     sp.set('status',     params.status);
        if (params?.action_key) sp.set('action_key', params.action_key);
        if (params?.page)       sp.set('page',       String(params.page));
        if (params?.per_page)   sp.set('per_page',   String(params.per_page));
        const qs = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/approval-requests${qs}`);
        return data.data;
    },

    createApprovalRequest: async (payload: {
        action_key: string; action_label: string;
        subject_type?: string; subject_id?: string; subject_label?: string;
        description?: string; context?: Record<string, unknown>;
    }): Promise<{ id: string }> => {
        const { data } = await apiClient.post<ApiResponse<{ id: string }>>('/api/institute-admin/approval-requests', payload);
        return data.data;
    },

    decideApprovalRequest: async (id: string, decision: 'approved' | 'rejected', comment?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/approval-requests/${id}/decide`, { decision, comment });
        return data.data;
    },

    withdrawApprovalRequest: async (id: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/approval-requests/${id}/withdraw`, {});
        return data.data;
    },

};

// ============================================
// Content Library API
// ============================================

export type ContentType = 'paper' | 'notes' | 'syllabus' | 'assignment' | 'resource';
export type ContentStatus = 'published' | 'draft' | 'archived';

export interface ContentItem {
    id: string;
    title: string;
    file_url: string;
    subject_id: string | null;
    downloads: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    created_by: string;
    content_type: ContentType;
    course_id: string | null;
    notes: string;
    status: ContentStatus;
    version: number;
    audience_type?: 'all_students' | 'selected_students' | 'all_institutes' | 'selected_institutes';
    audience_student_ids?: string[] | null;
    audience_institute_ids?: string[] | null;
    subject: { id: string; name: string } | null;
    course:  { id: string; name: string } | null;
    uploader: { id: string; name: string } | null;
}

export const contentLibraryApi = {
    list: async (params?: {
        content_type?: ContentType;
        course_id?: string;
        subject_id?: string;
        status?: ContentStatus;
        search?: string;
        page?: number;
        per_page?: number;
    }): Promise<{ data: ContentItem[]; pagination: { page: number; per_page: number; total: number; total_pages: number } }> => {
        const qs = buildQueryString(params);
        const { data } = await apiClient.get<ApiResponse<ContentItem[]>>(`/api/institute-admin/content-library${qs}`);
        return data as any;
    },
    create: async (payload: {
        title: string;
        content_type: ContentType;
        file_url: string;
        subject_id?: string | null;
        subject_ids?: string[];
        course_id?: string | null;
        notes?: string;
        status?: ContentStatus;
        audience_type?: 'all_students' | 'selected_students' | 'all_institutes' | 'selected_institutes';
        audience_student_ids?: string[];
        audience_institute_ids?: string[];
    }): Promise<ContentItem> => {
        const { data } = await apiClient.post<ApiResponse<ContentItem>>('/api/institute-admin/content-library', payload, { forceRemote: true } as any);
        return data.data;
    },
    update: async (id: string, payload: Partial<{
        title: string;
        content_type: ContentType;
        file_url: string;
        subject_id: string | null;
        subject_ids: string[];
        course_id: string | null;
        notes: string;
        status: ContentStatus;
        audience_type?: 'all_students' | 'selected_students' | 'all_institutes' | 'selected_institutes';
        audience_student_ids?: string[];
        audience_institute_ids?: string[];
    }>): Promise<ContentItem> => {
        const { data } = await apiClient.put<ApiResponse<ContentItem>>(`/api/institute-admin/content-library/${id}`, payload, { forceRemote: true } as any);
        return data.data;
    },
    delete: async (id: string, force = false): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/content-library/${id}${force ? '?force=true' : ''}`);
    },
};

// ============================================
// Student API (Student Module features)
// ============================================

export const studentApi = {
    // Profile
    getProfile: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/student/profile');
        return data.data;
    },

    // Dashboard Stats
    getDashboardStats: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/student/dashboard-stats');
        return data.data;
    },

    // Fee history
    getFees: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/student/fees');
        return data.data;
    },

    // Notices
    getNotices: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/student/notices');
        return data.data;
    },
    markNoticeRead: async (id: string): Promise<void> => {
        await apiClient.put(`/api/student/notices/${id}/read`);
    },

    // Attendance
    getAttendance: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/student/attendance');
        return data.data;
    },

    // Typing Lessons (Keyboard Drills)
    getTypingLessons: async (params?: { category?: string; language?: string; wpm_level?: number }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.category) searchParams.set('category', params.category);
        if (params?.language) searchParams.set('language', params.language);
        if (params?.wpm_level) searchParams.set('wpm_level', String(params.wpm_level));
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/student/typing-lessons${query}`);
        return data.data;
    },
    getTypingLesson: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/student/typing-lessons/${id}`);
        return data.data;
    },
    getTypingPracticeResults: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/student/typing-practice-results');
        return data.data;
    },
    submitTypingPractice: async (result: {
        lesson_id: string;
        accuracy: number;
        wpm: number;
        gross_wpm: number;
        net_wpm: number;
        time_taken_seconds: number;
        total_characters: number;
        correct_characters: number;
        mistakes: number;
        backspace_count?: number;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/student/typing-practice', result);
        return data.data;
    },

    // Typing Tests
    getTypingTests: async (params?: { type?: string; language?: string; wpm_level?: number }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.type) searchParams.set('type', params.type);
        if (params?.language) searchParams.set('language', params.language);
        if (params?.wpm_level) searchParams.set('wpm_level', String(params.wpm_level));
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/student/typing-tests${query}`);
        return data.data;
    },
    getTypingTest: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/student/typing-tests/${id}`);
        return data.data;
    },
    submitTypingTest: async (testId: string, result: {
        time_taken_seconds: number;
        mistakes: number;
        gross_wpm: number;
        net_wpm: number;
        accuracy: number;
        submitted_content: string;
        submitted_html?: string;
        language?: string;
        wpm_level?: number;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/student/typing-tests/${testId}/submit`, result);
        return data.data;
    },
    getTypingTestResults: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/student/typing-test-results');
        return data.data;
    },

    // Theory Tests (MCQ)
    getTheoryCategories: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/student/theory-tests/categories');
        return data.data;
    },
    getLearningResources: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/student/learning-resources');
        return data.data;
    },
    getVideoTutorial: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/student/video-tutorials/${id}`);
        return data.data;
    },
    getExamCatalog: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/student/exam-catalog');
        return data.data;
    },
    getOfflineExamBundle: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/student/offline-exam-bundle');
        return data.data;
    },
    getTheoryTests: async (params?: { category?: string; wpm_level?: number; limit?: number }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.category) searchParams.set('category', params.category);
        if (params?.wpm_level) searchParams.set('wpm_level', String(params.wpm_level));
        if (params?.limit) searchParams.set('limit', String(params.limit));
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/student/theory-tests${query}`);
        return data.data;
    },
    submitTheoryTest: async (submission: {
        category: string;
        answers: { question_id: string; selected_answer: string }[];
        time_taken_seconds: number;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/student/theory-tests/submit', submission);
        return data.data;
    },
    getTheoryTestResults: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/student/theory-test-results');
        return data.data;
    },

    // Combined Exam Results
    getExamResults: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/student/exam-results');
        return data.data;
    },
    getDesktopExamProofs: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/student/exam-sessions');
        return data.data ?? [];
    },
};

// ============================================
// Keyboard Practice API
// ============================================

export const keyboardPracticeApi = {
    getMasters: async (params?: { subject_id?: string; practice_type?: 'keyboard' | 'typing' }): Promise<any[]> => {
        const searchParams = new URLSearchParams();
        if (params?.subject_id) searchParams.set('subject_id', params.subject_id);
        if (params?.practice_type) searchParams.set('practice_type', params.practice_type);
        const query = searchParams.toString() ? `?${searchParams.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/keyboard-practice/masters${query}`);
        return data.data;
    },
    createMaster: async (masterData: { subject_id?: string; name: string; practice_type?: 'keyboard' | 'typing'; category_key?: string }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/keyboard-practice/masters', masterData);
        return data.data;
    },
    updateMaster: async (id: string, masterData: { subject_id?: string; name: string; practice_type?: 'keyboard' | 'typing'; category_key?: string }): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/keyboard-practice/masters/${id}`, masterData);
        return data.data;
    },
    deleteMaster: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/keyboard-practice/masters/${id}`);
    },
    getLessons: async (masterId?: string): Promise<any[]> => {
        const query = masterId ? `?master_id=${masterId}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/keyboard-practice/lessons${query}`);
        return data.data;
    },
    // Page-wise library load. Sends filters/sort/page so the server returns ONE
    // page instead of every lesson. Degrades gracefully: an older backend or the
    // offline EXE (which returns the full array, ignoring these params) is
    // detected via a missing `pagination` field, and the caller falls back to
    // client-side filtering/paging over the returned array.
    getLessonsPage: async (params: {
        practice_type?: 'keyboard' | 'typing';
        course_id?: string;
        subject_id?: string;
        category_key?: string;
        search?: string;
        sort?: 'newest' | 'oldest' | 'name_asc' | 'name_desc';
        page?: number;
        limit?: number;
    }): Promise<{ rows: any[]; pagination: { page: number; limit: number; total: number; total_pages: number } | null }> => {
        const sp = new URLSearchParams();
        if (params.practice_type) sp.set('practice_type', params.practice_type);
        if (params.course_id) sp.set('course_id', params.course_id);
        if (params.subject_id) sp.set('subject_id', params.subject_id);
        if (params.category_key) sp.set('category_key', params.category_key);
        if (params.search) sp.set('search', params.search);
        if (params.sort) sp.set('sort', params.sort);
        sp.set('page', String(params.page || 1));
        sp.set('limit', String(params.limit || 25));
        const { data } = await apiClient.get<ApiResponse<any[]> & { pagination?: any }>(`/api/keyboard-practice/lessons?${sp.toString()}`);
        return { rows: data.data || [], pagination: data.pagination || null };
    },
    createLesson: async (lessonData: { master_id: string; title: string; description?: string; content: string; allow_backspace?: boolean }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/keyboard-practice/lessons', lessonData);
        return data.data;
    },
    updateLesson: async (id: string, lessonData: Partial<{ title: string; description?: string; content: string; allow_backspace: boolean }>): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/keyboard-practice/lessons/${id}`, lessonData);
        return data.data;
    },
    deleteLesson: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/keyboard-practice/lessons/${id}`);
    },
    // Real master data (superadmin: platform-wide; institute admin: their own
    // institute on top) — replaces the previous hardcoded category enum.
    getCategories: async (practiceType?: 'keyboard' | 'typing'): Promise<any[]> => {
        const query = practiceType ? `?practice_type=${practiceType}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/keyboard-practice/categories${query}`);
        return data.data;
    },
    createCategory: async (categoryData: { practice_type: 'keyboard' | 'typing'; label: string; category_key?: string }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/keyboard-practice/categories', categoryData);
        return data.data;
    },
    updateCategory: async (id: string, categoryData: { label: string; sort_order?: number }): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/keyboard-practice/categories/${id}`, categoryData);
        return data.data;
    },
    deleteCategory: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/keyboard-practice/categories/${id}`);
    },
};

// ============================================
// Policy Templates API (super_admin only)
// ============================================

export const policyTemplatesApi = {
    list: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/policy-templates');
        return data.data ?? [];
    },
    get: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/policy-templates/${id}`);
        return data.data;
    },
    create: async (payload: { name: string; description?: string; config: any; is_default?: boolean }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/policy-templates', payload);
        return data.data;
    },
    update: async (id: string, payload: Partial<{ name: string; description: string; config: any; is_default: boolean }>): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/policy-templates/${id}`, payload);
        return data.data;
    },
    delete: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/policy-templates/${id}`);
    },
    applyToInstitute: async (templateId: string, instituteId: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/policy-templates/${templateId}/apply`, { institute_id: instituteId });
        return data.data;
    },
};

// ============================================
// Audit Register API (super_admin only)
// ============================================

export const auditApi = {
    list: async (params?: {
        category?: string; event_type?: string; actor_role?: string;
        institute_id?: string; from_date?: string; to_date?: string;
        q?: string; page?: number; limit?: number;
    }): Promise<any> => {
        const qs = new URLSearchParams();
        if (params?.category)     qs.set('category',     params.category);
        if (params?.event_type)   qs.set('event_type',   params.event_type);
        if (params?.actor_role)   qs.set('actor_role',   params.actor_role);
        if (params?.institute_id) qs.set('institute_id', params.institute_id);
        if (params?.from_date)    qs.set('from_date',    params.from_date);
        if (params?.to_date)      qs.set('to_date',      params.to_date);
        if (params?.q)            qs.set('q',            params.q);
        if (params?.page)         qs.set('page',         String(params.page));
        if (params?.limit)        qs.set('limit',        String(params.limit));
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/audit-register?${qs}`);
        return data;
    },
    stats: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/audit-register/stats');
        return data.data;
    },
    distinct: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/audit-register/distinct');
        return data.data;
    },
    clearAll: async (before?: string): Promise<{ deleted: number }> => {
        const qs = before ? `?before=${encodeURIComponent(before)}` : '';
        const { data } = await apiClient.delete<ApiResponse<{ deleted: number }>>(`/api/audit-register${qs}`);
        return data.data ?? { deleted: 0 };
    },
};

// ============================================
// Class Execution API (instructor attendance & monitoring)
// ============================================

export const classSessionsApi = {
    list: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/class-sessions');
        return data.data ?? [];
    },

    start: async (payload: {
        subject_id?: string;
        session_id?: string;
        class_label?: string;
        notes?: string;
    }): Promise<{ id: string; started_at: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/class-sessions', payload);
        return data.data;
    },

    get: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/class-sessions/${id}`);
        return data.data;
    },

    saveMarks: async (id: string, marks: Array<{
        student_id: string;
        attendance_status: 'present' | 'absent' | 'late' | 'excused';
        remark?: string;
        flagged?: boolean;
        flag_reason?: string;
    }>): Promise<{ saved: number }> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/class-sessions/${id}/marks`, { marks });
        return data.data;
    },

    close: async (id: string, notes?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/class-sessions/${id}/close`, { notes });
        return data.data;
    },

    raiseIssue: async (id: string, description: string, student_name?: string): Promise<{ approval_request_id: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/class-sessions/${id}/raise-issue`, {
            description,
            student_name,
        });
        return data.data;
    },
};

// ============================================
// Senior Instructor Supervision API
// ============================================

export const seniorInstructorApi = {
    getOverview: async (): Promise<{
        instructors: any[];
        subject_stats: any[];
        recent_sessions: any[];
        open_issues: any[];
        summary: {
            instructor_count: number;
            subject_count: number;
            total_students: number;
            sessions_30d: number;
            pending_issues: number;
        };
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/senior-instructor/overview');
        return data.data ?? { instructors: [], subject_stats: [], recent_sessions: [], open_issues: [], summary: { instructor_count: 0, subject_count: 0, total_students: 0, sessions_30d: 0, pending_issues: 0 } };
    },

    getInstructorDetail: async (userId: string): Promise<{
        profile: any;
        assignments: any[];
        sessions: any[];
        flagged: any[];
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/senior-instructor/instructor-detail/${userId}`);
        return data.data ?? { profile: null, assignments: [], sessions: [], flagged: [] };
    },
};

// ============================================
// Front Office API (admin_staff workspace)
// ============================================

export const frontOfficeApi = {
    getOverview: async (): Promise<{
        recent_registrations: any[];
        document_pending: any[];
        active_notices: any[];
        today_sessions: any[];
        fee_due: any[];
        summary: {
            total_students: number;
            new_today: number;
            doc_pending_count: number;
            active_notice_count: number;
            open_sessions_today: number;
            fee_due_count: number;
        };
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/front-office/overview');
        return data.data ?? {
            recent_registrations: [], document_pending: [], active_notices: [],
            today_sessions: [], fee_due: [],
            summary: { total_students: 0, new_today: 0, doc_pending_count: 0, active_notice_count: 0, open_sessions_today: 0, fee_due_count: 0 },
        };
    },
};

// ============================================
// Accountant Workspace API
// ============================================

export const accountantApi = {
    getOverview: async (): Promise<{
        summary: {
            collections_today: number;
            collections_month: number;
            receipts_today: number;
            receipts_month: number;
            outstanding_balance: number;
            due_student_count: number;
            overdue_student_count: number;
            pending_approval_count: number;
        };
        recent_collections: any[];
        due_students: any[];
        monthly_trend: any[];
        pending_approvals: any[];
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/accountant/overview');
        return data.data ?? {
            summary: { collections_today: 0, collections_month: 0, receipts_today: 0, receipts_month: 0, outstanding_balance: 0, due_student_count: 0, overdue_student_count: 0, pending_approval_count: 0 },
            recent_collections: [], due_students: [], monthly_trend: [], pending_approvals: [],
        };
    },

    getPayablesOverview: async (params?: { enrollment_type?: string }): Promise<{
        summary: { total_owed: number; total_paid: number; total_pending: number; pending_count: number; partial_count: number; paid_count: number };
        pending_entries: any[];
        receipts: any[];
        recent_failed_license_attempts?: any[];
        monthly_reconciliation: any[];
        by_subject: any[];
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/accountant/payables-overview', { params: params?.enrollment_type ? { enrollment_type: params.enrollment_type } : undefined });
        return data.data ?? {
            summary: { total_owed: 0, total_paid: 0, total_pending: 0, pending_count: 0, partial_count: 0, paid_count: 0 },
            pending_entries: [], receipts: [], recent_failed_license_attempts: [], monthly_reconciliation: [], by_subject: [],
        };
    },

    recordLicensePayment: async (id: string, body: {
        amount: number;
        payment_date?: string;
        receipt_number?: string;
        payment_method?: string;
        notes?: string;
    }): Promise<any> => {
        // Offline-first: in the server EXE this routes through the local-api-call
        // handler (main.cjs), which applies the payment to the local ledger so it
        // shows immediately AND queues it to the outbox so it syncs to the cloud
        // (where the super-admin dashboard reads it). forceRemote used to bypass
        // that handler entirely, so an EXE-recorded licence payment reached
        // NEITHER the local view NOR the cloud — the ledger stayed ₹0 in both.
        const { data } = await apiClient.patch<ApiResponse<any>>(`/api/institute-admin/license-fee-ledger/${id}/record-payment`, body);
        return data.data ?? null;
    },
    // Pay several license-fee rows at once. Default per row = its full outstanding
    // balance; pass `amounts` to override a specific id. Returns { receipts[],
    // errors[], total_amount, paid_count, skipped_count } — the page builds one
    // combined PDF (a page per receipt) from `receipts`.
    bulkRecordLicensePayment: async (body: {
        ids: string[];
        payment_date?: string;
        payment_method?: string;
        notes?: string;
        amounts?: Record<string, number>;
    }): Promise<{ receipts: any[]; errors: { ledger_id: string; error: string }[]; total_amount: number; paid_count: number; skipped_count: number }> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/license-fee-ledger/bulk-record-payment', body);
        return data.data ?? { receipts: [], errors: [], total_amount: 0, paid_count: 0, skipped_count: 0 };
    },
    addLicenseLedgerNote: async (id: string, notes: string): Promise<any> => {
        const { data } = await apiClient.patch<ApiResponse<any>>(`/api/institute-admin/license-fee-ledger/${id}/note`, { notes });
        return data.data;
    },

    exportDailyCollections: async (date: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/accountant/export/daily-collections?date=${date}`);
        return (data as any).data ?? [];
    },
    exportDuesRegister: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/accountant/export/dues-register');
        return (data as any).data ?? [];
    },
    exportLicensePayables: async (status = 'all'): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/accountant/export/license-payables?status=${status}`);
        return (data as any).data ?? [];
    },
    exportMonthReceipts: async (month: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/accountant/export/month-receipts?month=${month}`);
        return (data as any).data ?? [];
    },
    exportAudit: async (from: string, to: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/accountant/export/audit?from=${from}&to=${to}`);
        return (data as any).data ?? [];
    },
};

// ============================================
// Counselor Workspace API
// ============================================

export const counselorApi = {
    getOverview: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/counselor/overview');
        return data.data ?? {};
    },
    listInquiries: async (params?: { stage?: string; q?: string }): Promise<any[]> => {
        const qs = new URLSearchParams();
        if (params?.stage && params.stage !== 'all') qs.set('stage', params.stage);
        if (params?.q) qs.set('q', params.q);
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/counselor/inquiries?${qs}`);
        return data.data ?? [];
    },
    createInquiry: async (body: {
        full_name: string; email?: string; mobile?: string; city?: string;
        source?: string; interested_course_id?: string; interested_subject?: string;
        follow_up_date?: string; follow_up_note?: string;
    }): Promise<{ id: string }> => {
        const { data } = await apiClient.post<ApiResponse<{ id: string }>>('/api/institute-admin/counselor/inquiries', body);
        return data.data ?? { id: '' };
    },
    updateInquiry: async (id: string, body: {
        stage?: string; follow_up_date?: string; follow_up_note?: string;
        lost_reason?: string; converted_student_id?: string;
    }): Promise<void> => {
        await apiClient.patch(`/api/institute-admin/counselor/inquiries/${id}`, body);
    },
    getNotes: async (inquiryId: string): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/counselor/notes/${inquiryId}`);
        return data.data ?? [];
    },
    addNote: async (body: {
        inquiry_id?: string; student_id?: string;
        note_type?: string; note_text: string; is_private?: boolean;
    }): Promise<void> => {
        await apiClient.post('/api/institute-admin/counselor/notes', body);
    },
};

// ============================================
// Platform Health Monitor API (super_admin only)
// ============================================

export const desktopUpdatesApi = {
    getStatus: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/desktop-updates/status');
        return data.data ?? {};
    },
    // `variant` is optional and deprecated: the LAB EXE now builds a single smart
    // installer (Win 7–11) and the Server EXE is a single x64 build, both on the
    // 'latest' channel. It is kept only for manual/legacy per-arch rebuilds.
    trigger: async (payload: { app: 'server' | 'client' | 'both'; bump: 'patch' | 'minor' | 'major' | 'none'; variant?: 'latest' | 'legacy'; notes?: string }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/desktop-updates/trigger', payload);
        return data.data ?? {};
    },
    getInstitutes: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/desktop-updates/institutes');
        return data.data ?? {};
    },
    // Any logged-in institute user: the currently-LIVE installers to download
    // (Server EXE + Exam Client, latest + legacy) with public download URLs.
    getDownloads: async (): Promise<Array<{ app: string; channel: string; version: string; file_name: string; file_size?: number; created_at?: string; url: string }>> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/desktop-updates/downloads');
        return (data.data ?? []) as any;
    },
    // FULL version history: every stored build for every app/channel (active or
    // not), newest first, each with a direct feed download url — so any prior
    // build can be downloaded at any time without a rebuild.
    getHistory: async (): Promise<Array<{ version: string; app: string; channel: string; file_name: string; file_size?: number; sha512?: string; created_at?: string; release_notes?: string; git_commit?: string; is_active: boolean; file_exists?: boolean; url: string }>> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/desktop-updates/history');
        return (data.data ?? []) as any;
    },
    deleteRelease: async (id: string, force?: boolean): Promise<any> => {
        const { data } = await apiClient.delete<ApiResponse<any>>(`/api/desktop-updates/release/${id}${force ? '?force=1' : ''}`);
        return data.data ?? {};
    },
    // Public, no-auth direct download URL (the electron-updater feed endpoint) —
    // works in a plain browser tab, avoiding the authenticated-blob download.
    publicDownloadUrl: (app: string, channel: string, fileName: string): string =>
        `${window.location.origin}/api/desktop-updates/feed/${encodeURIComponent(app)}/${encodeURIComponent(channel || 'latest')}/${encodeURIComponent(fileName)}`,
    prune: async (payload: { app?: string; channel?: string } = {}): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/desktop-updates/prune', payload);
        return data.data ?? {};
    },
    deleteJob: async (id: string): Promise<any> => {
        const { data } = await apiClient.delete<ApiResponse<any>>(`/api/desktop-updates/job/${id}`);
        return data.data ?? {};
    },
    cancelJob: async (id: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/desktop-updates/job/${id}/cancel`);
        return data.data ?? {};
    },
    download: async (id: string, fileName: string): Promise<void> => {
        const res = await apiClient.get(`/api/desktop-updates/download/${id}`, { responseType: 'blob' });
        const blob = res.data instanceof Blob ? res.data : new Blob([res.data as any]);
        // The server may return a JSON error body as a blob (404 / not found) —
        // surface its message instead of "downloading" an error file.
        if (blob.type && blob.type.includes('application/json')) {
            const text = await blob.text().catch(() => '');
            let msg = 'Download failed'; try { msg = JSON.parse(text)?.error?.message || msg; } catch { /* keep */ }
            throw new Error(msg);
        }
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName || 'installer.exe';
        a.style.display = 'none';
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        // Defer cleanup — revoking immediately can cancel the download in some browsers.
        setTimeout(() => { try { a.remove(); window.URL.revokeObjectURL(url); } catch { /* ignore */ } }, 2000);
    },
};

/**
 * Remote diagnostics + safe actions on an institute's Server EXE. The EXE polls
 * the cloud outbound, so these calls queue work rather than reaching the
 * institute directly — hence the queue-then-poll shape.
 */
export const exeRemoteApi = {
    /** The allow-listed action catalog (never hardcode it in the UI). */
    actions: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/exe-remote/actions');
        return data.data ?? [];
    },
    /** Every institute with its agent's live state. */
    agents: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/exe-remote/agents');
        return data.data ?? [];
    },
    history: async (instituteId: string, limit = 25): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/exe-remote/${instituteId}/history?limit=${limit}`);
        return data.data ?? [];
    },
    /** Queue an action; returns the command id to poll for a result. */
    run: async (instituteId: string, action: string, params?: Record<string, unknown>): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/exe-remote/${instituteId}/command`, { action, params: params ?? {} });
        return data.data;
    },
    result: async (instituteId: string, commandId: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/exe-remote/${instituteId}/command/${commandId}`);
        return data.data;
    },
    cancel: async (instituteId: string, commandId: string): Promise<void> => {
        await apiClient.post(`/api/exe-remote/${instituteId}/command/${commandId}/cancel`, {});
    },
};

export const healthMonitorApi = {
    getOverview: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/health-monitor/overview');
        return data.data ?? {};
    },
    getEvents: async (params?: {
        type?: string; severity?: string; unresolved?: boolean; unread?: boolean;
        route?: string; from?: string; to?: string; limit?: number; offset?: number;
    }): Promise<{ events: any[]; total: number; limit: number; offset: number }> => {
        const qs = new URLSearchParams();
        if (params?.type)       qs.set('type', params.type);
        if (params?.severity)   qs.set('severity', params.severity);
        if (params?.unresolved) qs.set('unresolved', '1');
        if (params?.unread)     qs.set('unread', '1');
        if (params?.route)      qs.set('route', params.route);
        if (params?.from)       qs.set('from', params.from);
        if (params?.to)         qs.set('to', params.to);
        if (params?.limit)      qs.set('limit', String(params.limit));
        if (params?.offset)     qs.set('offset', String(params.offset));
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/health-monitor/events?${qs}`);
        return data.data ?? { events: [], total: 0, limit: 100, offset: 0 };
    },
    resolveEvent: async (id: string): Promise<void> => {
        await apiClient.patch(`/api/health-monitor/events/${id}/resolve`);
    },
    markEventRead: async (id: string): Promise<void> => {
        await apiClient.patch(`/api/health-monitor/events/${id}/read`);
    },
    deleteEvent: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/health-monitor/events/${id}`);
    },
    clearResolved: async (): Promise<{ deleted: number }> => {
        const { data } = await apiClient.delete<ApiResponse<{ deleted: number }>>('/api/health-monitor/events/resolved');
        return data.data ?? { deleted: 0 };
    },
    clearAll: async (): Promise<{ deleted: number }> => {
        const { data } = await apiClient.delete<ApiResponse<{ deleted: number }>>('/api/health-monitor/events/all');
        return data.data ?? { deleted: 0 };
    },
    getStats: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/health-monitor/stats');
        return data.data ?? {};
    },
};

// ============================================
// HR Workspace API (hr role)
// Salary is never returned — HR sees people/lifecycle data only.
// ============================================

export const hrApi = {
    getOverview: async (): Promise<{
        totals: { total: number; active: number; on_leave: number; inactive: number; terminated: number };
        by_department: Array<{ department: string; count: number }>;
        recent_joins: any[];
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/hr/overview');
        return data.data ?? {
            totals: { total: 0, active: 0, on_leave: 0, inactive: 0, terminated: 0 },
            by_department: [],
            recent_joins: [],
        };
    },

    getStaff: async (params?: {
        status?: string;
        department?: string;
        search?: string;
        page?: number;
        per_page?: number;
    }): Promise<{ data: any[]; pagination: any }> => {
        const { data } = await apiClient.get<any>('/api/institute-admin/hr/staff', { params });
        return { data: data.data ?? [], pagination: data.pagination ?? {} };
    },

    getStaffMember: async (id: string): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/hr/staff/${id}`);
        return data.data;
    },

    updateStaffMember: async (id: string, payload: {
        notes?: string;
        department?: string;
        reporting_role?: string;
        document_checklist?: Record<string, boolean>;
        leaving_date?: string | null;
    }): Promise<void> => {
        await apiClient.put(`/api/institute-admin/hr/staff/${id}`, payload);
    },

    lifecycle: async (id: string, action: 'activate' | 'deactivate' | 'on_leave' | 'terminate', notes?: string, leaving_date?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/hr/staff/${id}/lifecycle`, { action, notes, leaving_date });
        return data.data;
    },

    proposeRole: async (staffProfileId: string, proposed_role: string, justification: string): Promise<{ approval_request_id: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/hr/staff/${staffProfileId}/propose-role`, { proposed_role, justification });
        return data.data;
    },

    getMyRequests: async (status?: string): Promise<any[]> => {
        const params = status && status !== 'all' ? { status } : {};
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/hr/my-requests', { params });
        return data.data ?? [];
    },

    withdrawRequest: async (id: string): Promise<void> => {
        await apiClient.post(`/api/institute-admin/approval-requests/${id}/withdraw`, {});
    },
};

// ============================================
// Receptionist Workspace API
// No fee/finance data. Student lookup, sessions, notices, issue escalation.
// ============================================

export const receptionistApi = {
    getOverview: async (): Promise<{
        summary: { total_students: number; active_students: number; sessions_today: number; open_sessions_today: number; active_notices: number };
        today_sessions: any[];
        active_notices: any[];
        recent_registrations: any[];
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/receptionist/overview');
        return data.data ?? {
            summary: { total_students: 0, active_students: 0, sessions_today: 0, open_sessions_today: 0, active_notices: 0 },
            today_sessions: [],
            active_notices: [],
            recent_registrations: [],
        };
    },

    lookup: async (q: string, page = 1): Promise<{ data: any[]; pagination: any }> => {
        const { data } = await apiClient.get<any>('/api/institute-admin/receptionist/student-lookup', { params: { q, page } });
        return { data: data.data ?? [], pagination: data.pagination ?? {} };
    },

    escalate: async (payload: {
        subject_label: string;
        description: string;
        student_user_id?: string;
        category?: 'attendance' | 'document' | 'batch_change' | 'access_issue' | 'general';
    }): Promise<{ approval_request_id: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/receptionist/escalate', payload);
        return data.data;
    },

    getMyEscalations: async (): Promise<any[]> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/receptionist/my-escalations');
        return data.data ?? [];
    },
};

// ============================================
// Block Governance API (super_admin only)
// ============================================

export const blockGovernanceApi = {
    list: async (params?: {
        block_type?: string;
        institute_id?: string;
        blocked_by_role?: string;
        q?: string;
    }): Promise<any[]> => {
        const qs = new URLSearchParams();
        if (params?.block_type)      qs.set('block_type',      params.block_type);
        if (params?.institute_id)    qs.set('institute_id',    params.institute_id);
        if (params?.blocked_by_role) qs.set('blocked_by_role', params.blocked_by_role);
        if (params?.q)               qs.set('q', params.q);
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/block-governance?${qs}`);
        return data.data ?? [];
    },
    summary: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/block-governance/summary');
        return data.data;
    },
    distinct: async (): Promise<any> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/block-governance/distinct');
        return data.data;
    },
};

// ============================================
// Instructor Workspace API
// ============================================

export const instructorApi = {
    getDashboard: async (): Promise<{
        assignments: any[];
        summary: { subject_count: number; student_count: number; active_session_count: number };
        active_sessions: any[];
    }> => {
        const { data } = await apiClient.get<ApiResponse<any>>('/api/institute-admin/instructor/dashboard');
        // Defaults are MERGED, not used only when the whole answer is missing.
        // The Institute EXE answers a path it cannot serve offline with `{}` --
        // truthy, so the old `??` kept it, and the page then read
        // `summary.subject_count` off undefined and threw, which until the
        // error boundary went in blanked the entire portal window.
        const d = (data.data && typeof data.data === 'object') ? data.data : {};
        return {
            assignments: Array.isArray(d.assignments) ? d.assignments : [],
            summary: {
                subject_count: Number(d.summary?.subject_count) || 0,
                student_count: Number(d.summary?.student_count) || 0,
                active_session_count: Number(d.summary?.active_session_count) || 0,
            },
            active_sessions: Array.isArray(d.active_sessions) ? d.active_sessions : [],
        };
    },

    getStudents: async (params?: { subject_id?: string; search?: string; page?: number; per_page?: number }): Promise<any> => {
        const sp = new URLSearchParams();
        if (params?.subject_id) sp.set('subject_id', params.subject_id);
        if (params?.search)     sp.set('search',     params.search);
        if (params?.page)       sp.set('page',       String(params.page));
        if (params?.per_page)   sp.set('per_page',   String(params.per_page));
        const q = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/instructor/my-students${q}`);
        return data.data;
    },

    getMySubjects: async (): Promise<{ subject_id: string; subject_name: string; course_name?: string }[]> => {
        const { data } = await apiClient.get<ApiResponse<any[]>>('/api/institute-admin/instructor/my-subjects');
        return data.data ?? [];
    },

    getAssignments: async (instructorUserId?: string): Promise<any[]> => {
        const q = instructorUserId ? `?instructor_user_id=${instructorUserId}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/instructor-assignments${q}`);
        return data.data ?? [];
    },

    createAssignment: async (payload: {
        instructor_user_id: string;
        subject_id: string;
        session_id?: string;
        notes?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/instructor-assignments', payload);
        return data.data;
    },

    deleteAssignment: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/instructor-assignments/${id}`);
    },

    createTypingLesson: async (payload: {
        subject_id: string; category: string; lesson_name: string;
        lesson_number: number; lesson_content: string; language?: string; wpm_level?: number;
        practice_type_id?: string;
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/typing-lessons', payload);
        return data.data;
    },

    updateTypingLesson: async (id: string, payload: Record<string, unknown>): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/typing-lessons/${id}`, payload);
        return data.data;
    },

    submitTypingLesson: async (id: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/typing-lessons/${id}/submit`, {});
        return data.data;
    },

    deleteTypingLesson: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/typing-lessons/${id}`);
    },

    createTheoryTest: async (payload: {
        subject_id: string; category: string; question: string;
        option_a: string; option_b: string; option_c: string; option_d: string;
        correct_answer: 'A' | 'B' | 'C' | 'D'; difficulty?: 'easy' | 'medium' | 'hard';
    }): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/theory-tests', payload);
        return data.data;
    },

    updateTheoryTest: async (id: string, payload: Record<string, unknown>): Promise<any> => {
        const { data } = await apiClient.put<ApiResponse<any>>(`/api/institute-admin/theory-tests/${id}`, payload);
        return data.data;
    },

    submitTheoryTest: async (id: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/theory-tests/${id}/submit`, {});
        return data.data;
    },

    deleteTheoryTest: async (id: string): Promise<void> => {
        await apiClient.delete(`/api/institute-admin/theory-tests/${id}`);
    },
};

// ============================================
// Content Review API (admin / principal)
// ============================================

export const contentReviewApi = {
    list: async (params?: { type?: string; status?: string }): Promise<any[]> => {
        const sp = new URLSearchParams();
        if (params?.type)   sp.set('type',   params.type);
        if (params?.status) sp.set('status', params.status);
        const qs = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/content-review${qs}`);
        return data.data ?? [];
    },

    decide: async (type: 'typing_lesson' | 'theory_test', id: string, decision: 'published' | 'rejected', review_note?: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/content-review/${type}/${id}/decide`, { decision, review_note });
        return data.data;
    },

    getTypingLessons: async (params?: { subject_id?: string; status?: string }): Promise<any[]> => {
        const sp = new URLSearchParams();
        if (params?.subject_id) sp.set('subject_id', params.subject_id);
        if (params?.status)     sp.set('status',     params.status);
        const qs = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/typing-lessons${qs}`);
        return data.data ?? [];
    },

    getTheoryTests: async (params?: { subject_id?: string; status?: string }): Promise<any[]> => {
        const sp = new URLSearchParams();
        if (params?.subject_id) sp.set('subject_id', params.subject_id);
        if (params?.status)     sp.set('status',     params.status);
        const qs = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any[]>>(`/api/institute-admin/theory-tests${qs}`);
        return data.data ?? [];
    },
};

// ============================================
// Approvals API (principal-governed approval queue)
// ============================================

export const approvalsApi = {
    list: async (params?: { status?: string; action_key?: string; page?: number; per_page?: number }): Promise<any> => {
        const sp = new URLSearchParams();
        if (params?.status)     sp.set('status',     params.status);
        if (params?.action_key) sp.set('action_key', params.action_key);
        if (params?.page)       sp.set('page',       String(params.page));
        if (params?.per_page)   sp.set('per_page',   String(params.per_page));
        const q = sp.toString() ? `?${sp.toString()}` : '';
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/approval-requests${q}`);
        // Backend returns { requests: [...], pagination }. Always hand back the
        // array (never the wrapper object) so callers can safely .map/.filter it.
        const d: any = data.data;
        if (Array.isArray(d)) return d;
        return d?.requests ?? d?.data ?? [];
    },

    summary: async (): Promise<{ total_pending: number; by_action: { action_key: string; cnt: string }[] }> => {
        const sp = new URLSearchParams({ status: 'pending', per_page: '1' });
        const { data } = await apiClient.get<ApiResponse<any>>(`/api/institute-admin/approval-requests?${sp}`);
        const raw = data.data;
        const total_pending: number = raw?.pagination?.total ?? 0;
        const by_action: { action_key: string; cnt: string }[] = [];
        return { total_pending, by_action };
    },

    create: async (payload: {
        action_key: string;
        action_label?: string;
        subject_type?: string;
        subject_id?: string;
        subject_label?: string;
        description?: string;
        context?: Record<string, unknown>;
    }): Promise<{ id: string; created_at: string }> => {
        const { data } = await apiClient.post<ApiResponse<any>>('/api/institute-admin/approval-requests', payload);
        return data.data;
    },

    decide: async (id: string, decision: 'approved' | 'rejected', decision_comment: string): Promise<any> => {
        const { data } = await apiClient.post<ApiResponse<any>>(`/api/institute-admin/approval-requests/${id}/decide`, {
            decision,
            comment: decision_comment,
        });
        return data.data;
    },

    withdraw: async (id: string): Promise<void> => {
        await apiClient.post(`/api/institute-admin/approval-requests/${id}/withdraw`, {});
    },
};

// ── Error / admin reports ────────────────────────────────────────────────────
export interface ErrorReportPayload {
    reporter_name?: string;
    reporter_email?: string;
    institute_id?: string | null;
    institute_name?: string;
    error_text: string;
    screenshot_data?: string | null;
    source?: 'login' | 'institute' | 'server_exe';
}

export const errorReportsApi = {
    // Submit works BEFORE sign-in. In the Server EXE we post through a dedicated
    // IPC that hits the cloud directly (the login screen has no session, so the
    // normal local-API adapter would reject the call); everywhere else we POST
    // the public endpoint straight to the cloud.
    submit: async (payload: ErrorReportPayload): Promise<{ id: string; created_at: string }> => {
        const exe = (typeof window !== 'undefined' ? window.examClient?.server : undefined) as any;
        if (exe?.submitErrorReport) {
            const res = await exe.submitErrorReport(payload);
            if (!res?.success) throw new Error(res?.error || 'Failed to submit report.');
            return res.data;
        }
        const { data } = await apiClient.post<ApiResponse<any>>('/api/error-reports', payload, { forceRemote: true } as any);
        return data.data;
    },

    list: async (params?: { status?: 'open' | 'resolved'; page?: number; per_page?: number }): Promise<{
        data: any[]; pagination: { page: number; per_page: number; total: number }; summary: { open: number; resolved: number };
    }> => {
        const sp = new URLSearchParams();
        if (params?.status) sp.set('status', params.status);
        if (params?.page) sp.set('page', String(params.page));
        if (params?.per_page) sp.set('per_page', String(params.per_page));
        const q = sp.toString() ? `?${sp}` : '';
        const { data } = await apiClient.get<any>(`/api/error-reports${q}`, { forceRemote: true } as any);
        return { data: data.data ?? [], pagination: data.pagination ?? { page: 1, per_page: 50, total: 0 }, summary: data.summary ?? { open: 0, resolved: 0 } };
    },

    resolve: async (id: string, action: 'resolve' | 'reopen' = 'resolve', resolution_note?: string): Promise<any> => {
        const { data } = await apiClient.patch<ApiResponse<any>>(`/api/error-reports/${id}`, { action, resolution_note }, { forceRemote: true } as any);
        return data.data;
    },
};

export default apiClient;
